import { useEffect, useState, useContext, useRef, useMemo } from "react";
import { useSearchParams, useNavigate, useParams } from "react-router";

import { AppContext } from '../../context.js';
import { FilePlus, Chat as ChatIcon, MoreVertical, ChevronUp, ChevronDown, FileEdit, ArrowDown, UserCheck, Tag, Delete, Alert, CheckLarge, Lock, Plus, Close } from "../../files/icons/index.js";
import { PatientAvatar, Status, Button, Counter } from "../components/component.js";
import Loading from "../components/loading.js";
import axios from "axios";
import Drop from "../components/drop.js";
import PhysicianPicker from "../components/physicianPicker.js";
import PatientPicker from "../components/patientPicker.js";
import { EditableDatePicker, DateSelector } from "../components/datePicker.js";
import Editor from "../components/editor.js";
import Chat from "../components/chat.js";
import Helmet from "../components/helmet.js";

import { FilesPreview } from "../components/filePreview.js";
import { DatetimeToDate, DateToTimestamp, DateToFormat, calculateAge, programMonths, FirstDayOfMonth, LastDayOfMonth, formatDateYMD } from "../../utils/datetime.js";

import ProjectTasks from "../components/projectTasks.js";
import AppointmentCard from "../components/appointmentCard.js";
import AssessmentCard from "../components/assessmentCard.js";
import AssessmentFormModal from "../components/assessmentFormModal.js";
import PlanSectionCard from "../components/planSectionCard.js";
import { renderFormLine } from "../../utils/form.js";
import { groupByPerformanceYear, performanceYearOf, capForYear } from '../../utils/respite.js';
import { COGNITIVE_STAGING_TEMPLATE_IDS, isCognitiveStagingTemplate } from '../../utils/assessments.js';
import { isPatientFieldMissing, isCaregiverFieldMissing } from "../../utils/paafRequired.js";

function Patient() {
  const context = useContext(AppContext);

  let { id } = useParams();

  let patientID = id;

  const [searchParams, setSearchParams] = useSearchParams();

  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);

  const [patient, setPatient] = useState(null);
  const [users, setUsers] = useState([]);
  const [plans, setPlans] = useState([]);
  const [selectedPlanID, setSelectedPlanID] = useState(null);
  const [projects, setProjects] = useState([]);
  const [appointments, setAppointments] = useState([]);
  const [assessments, setAssessments] = useState([]);
  // Tracks whether the patient already has an active (non-canceled) FAST/CDR
  // assessment, independent of the Assessments-tab status/assignee filters,
  // so the Cognitive Staging action can show replace-vs-create copy.
  const [hasCognitiveStaging, setHasCognitiveStaging] = useState(false);
  const [medications, setMedications] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [settings, setSettings] = useState(null);
  const [partners, setPartners] = useState([]);

  // Workflow steps: profile (Patient Info), caregivers (Caregiver Info), preAlignment, postAlignment.
  // Remaining tabs: careplan, appointments, assessments, accounts (Respite), documents.
  // Legacy "projects" URLs (and no tab at all) resolve to whichever workflow step the
  // patient's lifecycle is currently at, set once patient data loads (see the fetch effect below).
  const initialTab = searchParams.get("tab");
  const [actionTabFilter, setActionTabFilter] = useState(initialTab === "projects" || !initialTab ? "preAlignment" : initialTab);

  // Respite accordion — current PY expanded by default
  const currentRespitePYStart = performanceYearOf(new Date()).startYear;
  const [expandedRespiteYears, setExpandedRespiteYears] = useState([currentRespitePYStart]);



  // Appointment filter and sorter
  const [appointmentsFilter, setAppointmentsFilter] = useState({ patientID: [patientID], statusType: ["TO_DO", "IN_PROGRESS", "PAUSED", "COMPLETED", "CANCELED"], userID: [] });
  const [appointmentsSorter, setAppointmentsSorter] = useState({ field: "created", order: "desc" });

  const [closeAppointmentSorterDrop, setCloseAppointmentSorterDrop] = useState(0);
  const [closeAppointmentStatusFilterDrop, setCloseAppointmentStatusFilterDrop] = useState(0);
  const [closeAppointmentUserFilterDrop, setCloseAppointmentUserFilterDrop] = useState(0);



  // Assessment filter and sorter
  const [assessmentsFilter, setAssessmentsFilter] = useState({ patientID: [patientID], statusType: ["TO_DO", "IN_PROGRESS", "PAUSED", "COMPLETED", "CANCELED"], userID: [] });
  const [assessmentsSorter, setAssessmentsSorter] = useState({ field: "created", order: "desc" });

  const [closeAssessmentSorterDrop, setCloseAssessmentSorterDrop] = useState(0);
  const [closeAssessmentStatusFilterDrop, setCloseAssessmentStatusFilterDrop] = useState(0);
  const [closeAssessmentUserFilterDrop, setCloseAssessmentUserFilterDrop] = useState(0);

  // Refs
  const imageUploadRef = useRef(null);
  const fileUploadRef = useRef(null);

  const [closePatientStatusDrop, setClosePatientStatusDrop] = useState(0);

  // Create plan
  const [closeCreatePlanDrop, setCloseCreatePlanDrop] = useState(0);
  const [closePlanDrop, setClosePlanDrop] = useState(0);

  // Creating a new project
  const [closeCreateProjectDrop, setCloseCreateProjectDrop] = useState(0);
  const [createProjectGroup, setCreateProjectGroup] = useState(null);
  const [deleteProject, setDeleteProject] = useState({ visible: false, project: null });
  const [deleteCaregiver, setDeleteCaregiver] = useState({ visible: false, caregiver: null });
  const [monthlyCheckinsModal, setMonthlyCheckinsModal] = useState({ visible: false });
  const [createProject, setCreateProject] = useState({
    visible: false,
    template: null,
    userID: null,
    patientID: null,
    createdAt: null,
  });
  const [estModal, setEstModal] = useState({ visible: false, from: "", to: "", submitting: false });
  const estConvertAttempted = useRef(false);
  const [startPostAlignmentModal, setStartPostAlignmentModal] = useState({ visible: false, submitting: false, confirmed: false });

  // Unaligning the patient (cancels their active lifecycle project)
  const [unalignModal, setUnalignModal] = useState({ visible: false, project: null, status: null, submitting: false, date: '', reasonID: null });

  // Create assessment
  const [closeCreateAssessmentDrop, setCloseCreateAssessmentDrop] = useState(0);
  const [createAssessment, setCreateAssessment] = useState({
    visible: false,
    template: null,
    userID: null,
    //patientID: null,
  });
  const [cognitiveStagingForm, setCognitiveStagingForm] = useState({ visible: false, template: null, assessmentID: null });

  // Create appointment
  const [closeCreateAppointmentDrop, setCloseCreateAppointmentDrop] = useState(0);
  const [createAppointment, setCreateAppointment] = useState({
    visible: false,
    template: null,
    userID: null,
    title: null,
    description: null,
  });

  // Create respite (service usage) transaction
  const [createService, setCreateService] = useState({
    visible: false,
    account: null,
    partner: null,
    service: null,
    dates: [],
  });
  const [logDatesVisible, setLogDatesVisible] = useState(false);


  // Updating referral source
  const [updateReferralSource, setUpdateReferralSource] = useState({
    visible: false,
    selfReferral: false,
    partnerID: null,
    partnerName: null,
  });

  useEffect(() => {
    let u = context.addSpiner();
    axios.all([
      axios.post('/api/admin/patients', {
        filter: {
          id: [patientID],
        },
        includeMissingRequired: true,
      }),
      axios.post('/api/admin/physicians', {
        filter: {}
      }),
      axios.post('/api/admin/plans', {
        filter: {
          patientID: [patientID],
          closedAt: false,
        }
      }),
      axios.post('/api/admin/projects', {
        filter: {
          patientID: [patientID],
          archived: null, // Get both archived and not projects
        }
      }),
      axios.post('/api/admin/appointments', {
        filter: appointmentsFilter,
        sorter: appointmentsSorter,
      }),
      axios.post('/api/admin/assessments', {
        filter: assessmentsFilter,
        sorter: assessmentsSorter,
      }),
      axios.post('/api/admin/assessments', {
        filter: { patientID: [patientID], templateID: COGNITIVE_STAGING_TEMPLATE_IDS, statusType: ["TO_DO", "IN_PROGRESS", "PAUSED", "COMPLETED"] },
      }),
      axios.post('/api/admin/medications', {
        filter: {
          patientID: [patientID],
        }
      }),
      axios.post('/api/admin/accounts', {
        filter: {
          userID: [patientID],
        }
      }),
      axios.get('/api/admin/settings', {
      }),
      axios.post('/api/admin/partners', {
        filter: {}
      }),
    ]).then(axios.spread((patients, users, plans, projects, appointments, assessments, cognitiveStaging, medications, accounts, settings, partners) => {
      const patientData = patients.data.patients[0];
      setPatient(patientData);
      setUsers(users.data.physicians);

      // Default the workflow-step tab to wherever the patient's lifecycle currently sits,
      // unless the URL asked for a specific non-workflow tab.
      if (initialTab === "projects" || !initialTab) {
        const stage = patientData?.lifecycleStage;
        // Unaligned patients have both alignment steps locked, so default to
        // Patient Info instead of a locked step.
        setActionTabFilter(
          stage === 'POST_ALIGNMENT_NEW' || stage === 'POST_ALIGNMENT_EST' ? 'postAlignment'
          : stage === 'UNALIGNED' ? 'profile'
          : 'preAlignment'
        );
      }

      const plansList = plans.data.plans || [];
      setPlans(plansList);
      if (plansList.length > 0) {
        setSelectedPlanID(plansList[0].id);
      }
      setProjects(projects.data.projects);
      setAppointments(appointments.data.appointments);
      setAssessments(assessments.data.assessments);
      setHasCognitiveStaging((cognitiveStaging.data.assessments || []).length > 0);
      setMedications(medications.data.medications);
      setAccounts(accounts.data.accounts);

      setSettings(settings.data);
      setPartners(partners.data.partners);

      setLoading(false);
    })).catch((e) => {
      context.addError(e);
      context.removeSpiner(u);
    }).finally(() => {
      context.removeSpiner(u);
    });
  }, []);

  // Auto-convert New → Est at program month 7 (or show notice if cron already did).
  useEffect(() => {
    if (loading || !patient) return;
    ensureEstConversion();
  }, [loading, patient]);

  // Reload appointments when filter or sorter changes
  useEffect(() => {
    if (!loading && patient) {
      reloadAppointments();
    }
  }, [appointmentsFilter, appointmentsSorter]);

  // Reload assessments when filter or sorter changes
  useEffect(() => {
    if (!loading && patient) {
      reloadAssessments();
    }
  }, [assessmentsFilter, assessmentsSorter]);

  function reloadAppointments() {
    let u = context.addSpiner();
    axios.post('/api/admin/appointments', {
      filter: appointmentsFilter,
      sorter: appointmentsSorter,
    }).then((response) => {
      setAppointments(response.data.appointments);
    }).catch((e) => {
      context.addError(e);
    }).finally(() => {
      context.removeSpiner(u);
    });
  }

  function reloadAssessments() {
    let u = context.addSpiner();
    axios.post('/api/admin/assessments', {
      filter: assessmentsFilter,
      sorter: assessmentsSorter,
    }).then((response) => {
      setAssessments(response.data.assessments);
    }).catch((e) => {
      context.addError(e);
    }).finally(() => {
      context.removeSpiner(u);
    });
  }

  // Refetch whether the patient has an active FAST/CDR assessment, independent of the
  // Assessments-tab filters, so the Cognitive Staging action copy updates immediately
  // after one is created, completed, canceled, or replaced.
  function reloadCognitiveStagingStatus() {
    axios.post('/api/admin/assessments', {
      filter: { patientID: [patientID], templateID: COGNITIVE_STAGING_TEMPLATE_IDS, statusType: ["TO_DO", "IN_PROGRESS", "PAUSED", "COMPLETED"] },
    }).then((response) => {
      setHasCognitiveStaging((response.data.assessments || []).length > 0);
    }).catch((e) => {
      context.addError(e);
    });
  }

  function handleCognitiveStagingChange() {
    reloadCognitiveStagingStatus();
    reloadAssessments();
  }

  // Refetch the patient's projects so lifecycle-derived UI (workflow step completion /
  // lock state) stays in sync after a task change may have auto-completed a project.
  function reloadProjects() {
    axios.post('/api/admin/projects', {
      filter: {
        patientID: [patientID],
        archived: null,
      }
    }).then((response) => {
      setProjects(response.data.projects);
    }).catch((e) => {
      context.addError(e);
    });
  }

  // Patient
  function triggerProfileImageInput() {
    imageUploadRef.current.click();
  }

  function changeProfileImageInput(e) {
    var data = new FormData();
    data.append('userID', patient.id);
    data.append('image', e.target.files[0]);
    
    let u = context.addSpiner();
    axios.post('/api/admin/patients/updateProfileImage', data).then((response) => {
      setPatient({...patient, profileImageID: response.data.profileImageID});
    }).catch((e) => {
      context.addError(e);
      context.removeSpiner(u);
    }).finally(() => {
      context.removeSpiner(u);
      // Reset the file input so subsequent uploads trigger onChange
      e.target.value = '';
    });
  }

  function activePatient() {
    let u = context.addSpiner();
    axios.post('/api/admin/patients/activate', {
      id: patient.id
    }).then((response) => {
      setPatient({ 
        ...patient,
        statusType: "ACTIVE",
        archivedAt: null,
        archivedBy: null,
        archivedByName: null
      });
      setClosePatientStatusDrop(closePatientStatusDrop + 1);
    }).catch((e) => {
      context.addError(e);
      context.removeSpiner(u);
    }).finally(() => {
      context.removeSpiner(u);
    });
  }
  
  function archivePatient() {
    let u = context.addSpiner();
    axios.post('/api/admin/patients/archive', {
      id: patient.id
    }).then((response) => {
      setPatient({ 
        ...patient, 
        statusType: "ARCHIVED",
        archivedAt: response.data.archivedAt,
        archivedBy: response.data.archivedBy,
        archivedByName: response.data.archivedByName
      });
      setClosePatientStatusDrop(closePatientStatusDrop + 1);
    }).catch((e) => {
      context.addError(e);
      context.removeSpiner(u);
    }).finally(() => {
      context.removeSpiner(u);
    });
  }

  // Switch action tab
  function switchTab(tab) {
    setActionTabFilter(tab);
    setSearchParams({ tab });
    // Assessments can be created/completed from Care Plan task actions, so refetch
    // the list when entering this tab to pick up those changes.
    if (tab === "assessments") {
      reloadAssessments();
    }
  }

  // PAAF required-field helpers
  function patientAnswer(code) {
    if (code === "alignmentDate") {
      return patient.alignmentDate || "";
    }
    let f = patient.fields.find((x) => x.code === code);
    return f && f.answers.length > 0 ? f.answers[0] : "";
  }

  function isFieldMissing(code) {
    return isPatientFieldMissing(code, patientAnswer(code), {
      lifecycleStage: patient?.lifecycleStage,
      patientResidence: patientAnswer("patientResidence"),
    });
  }

  function missingPaafCodes() {
    let codes = [];
    settings.patientTemplate.pages.forEach((page) => {
      page.lines.forEach((line) => {
        line.forEach((lineField) => {
          if (isFieldMissing(lineField.code)) codes.push(lineField.code);
        });
      });
    });
    return codes;
  }

  // PAAF caregiver required-field helpers (primary caregiver only — Caregivers[0]).
  function caregiverAnswers(caregiver) {
    const answers = {};
    (caregiver.fields || []).forEach((f) => {
      answers[f.code] = f.answers && f.answers.length > 0 ? f.answers[0] : "";
    });
    return answers;
  }

  function missingPaafCaregiverCodes() {
    if (!patient?.caregivers?.length) return [];
    const caregiver = patient.caregivers[0];
    const answers = caregiverAnswers(caregiver);
    let codes = [];
    (caregiver.pages || settings.caregiverTemplate.pages).forEach((page) => {
      page.lines.forEach((line) => {
        line.forEach((lineField) => {
          if (isCaregiverFieldMissing(lineField.code, answers)) codes.push(lineField.code);
        });
      });
    });
    return codes;
  }

  // Caregiver Info phase warn: unanswered Yes/No counts as incomplete;
  // explicit No is complete; Yes without a caregiver record is 1 missing;
  // Yes with a record uses PAAF caregiver field gaps.
  function caregiverPhaseWarnCount() {
    if (!patient || patient.hasCaregiver == null) return 1;
    if (patient.hasCaregiver === false) return 0;
    if (!patient.caregivers?.length) return 1;
    return missingPaafCaregiverCodes().length;
  }

  function updateHasCaregiver(hasCaregiver) {
    if (!patient) return;
    if (hasCaregiver === false && patient.caregivers?.length > 0) return;
    if (patient.hasCaregiver === hasCaregiver) return;

    let u = context.addSpiner();
    axios.post('/api/admin/patients/updateHasCaregiver', {
      id: patient.id,
      hasCaregiver,
    }).then(() => {
      setPatient(prev => ({ ...prev, hasCaregiver }));
    }).catch((e) => {
      context.addError(e);
      context.removeSpiner(u);
    }).finally(() => {
      context.removeSpiner(u);
    });
  }

  // Render profile
  function renderPages() {
    return settings.patientTemplate.pages.map((page) => {
      return (
        <div key={page.id} className="card mb-2">
          <div className="card-header sm">
            <div className="typo-strong-175">{page.title}</div>
          </div>
          <div className="card-content sm">
            {page.lines.map((line, index) => renderLine(page.id, line, index, page.lines.length === index + 1))}
          </div>
        </div>
      );
    });
  }

  function renderLine(pageID, line, lineIndex, isLastLine) {
    // Line example
    //[{"code": "firstName", "size": 6}, {"code": "lastName", "size": 6}]

    return (
      <div key={"page_"+pageID+"_line_"+lineIndex} className={"row " + (isLastLine ? "" : "mb-2")}>
        {line.map((lineField, index) => {
          let field = settings.patientTemplate.fields.find((f) => f.code === lineField.code);
          let fieldPatient = patient.fields.find((f) => f.code === field.code);
          let answer = fieldPatient && fieldPatient.answers.length>0 ? fieldPatient.answers[0] : "n/a";
          if (field.code === "alignmentDate") {
            answer = patient.alignmentDate || "n/a";
          }
          if (field.code === "icd10Code" && answer === "n/a" && patient.claimInfo?.primaryDiagnosis) {
            answer = patient.claimInfo.primaryDiagnosis;
          }
          if (field.code === "tierAssignment" && patient.procedureCode) {
            answer = patient.procedureCode;
          }
          let missing = isFieldMissing(field.code);

          if (field.type === "date" && answer !== "n/a") {
            answer = DateToFormat(DatetimeToDate(answer), context.dateFormat);
          }

          return (
            <div key={"page_"+pageID+"_line_"+lineIndex+"_field_"+field.code} className={`col-xs-${lineField.size} py-0`}>
              <div
                className={"typo-strong-150" + (missing ? " text-red" : "")}
                style={missing ? { background: '#FCD9D9', borderRadius: 8, padding: '2px 6px', display: 'inline-block' } : undefined}
              >
                {field.title}:
              </div>
              <div className={"typo-normal-150" + (missing ? " text-red" : "")}>{answer}</div>
            </div>
          );
        })}
      </div>
    );
  }

  // Patient files
  function triggerFileInput() {
    fileUploadRef.current.click();
  }

  function changeFileInput(e) {
    if (e.target.files.length === 0) {
      return;
    }

    var data = new FormData();
    data.append('fileLength', e.target.files.length);
    data.append('patientID', patient.id)

    for (var i = 0; i < e.target.files.length; i++) {
      data.append('file'+i, e.target.files[i]);
    }
    
    let u = context.addSpiner();
    axios.post('/api/admin/patients/uploadFile', data).then((response) => {
      setPatient({...patient, files: patient.files.concat(response.data)});
    }).catch((e) => {
      context.addError(e);
      context.removeSpiner(u);
    }).finally(() => {
      context.removeSpiner(u);
      // Reset the file input so subsequent uploads trigger onChange
      e.target.value = '';
    });
  }

  // Plan creation functions
  function createPlanConfirm(templateID) {
    let u = context.addSpiner();
    axios.post('/api/admin/plans/create', {
      patientID: patient.id,
      templateID: templateID,
    }).then((response) => {
      setPlans([...plans, response.data]);
      setSelectedPlanID(response.data.id);
      setCloseCreatePlanDrop(prev => prev + 1);
    }).catch((e) => {
      context.addError(e);
      context.removeSpiner(u);
    }).finally(() => {
      context.removeSpiner(u);
    });
  }

  function handleSectionNoteAdded(planID, sectionID) {
    setPlans(prev => prev.map(p => {
      if (p.id !== planID) return p;
      return {
        ...p,
        sections: p.sections.map(s => s.id === sectionID ? { ...s, hasNotes: true } : s),
      };
    }));
  }

  function closePlanConfirm() {
    let u = context.addSpiner();
    axios.post('/api/admin/plans/close', {
      id: selectedPlanID,
    }).then((response) => {
      // Remove closed plan from the list since we only show active plans
      setPlans(plans.filter(p => p.id !== selectedPlanID));
      setClosePlanDrop(prev => prev + 1);
      
      // Auto-select next active plan if available
      const remainingPlans = plans.filter(p => p.id !== selectedPlanID);
      if (remainingPlans.length > 0) {
        setSelectedPlanID(remainingPlans[0].id);
      } else {
        setSelectedPlanID(null);
      }
    }).catch((e) => {
      context.addError(e);
      context.removeSpiner(u);
    }).finally(() => {
      context.removeSpiner(u);
    });
  }

  // Projects
  function alignmentMonths() {
    if (!patient.alignmentDate) return null;
    return programMonths(DatetimeToDate(patient.alignmentDate));
  }

  // Creates a project directly (no modal). Stays on the patient page.
  function submitCreateProject({ templateID, patientID, createdAt, onSuccess, onError }) {
    let u = context.addSpiner();
    axios.post('/api/admin/projects/create', {
      templateID,
      patientID,
      createdAt,
    }).then((response) => {
      if (onSuccess) {
        onSuccess(response.data.id);
      }
    }).catch((e) => {
      context.addError(e);
      if (onError) onError(e);
    }).finally(() => {
      context.removeSpiner(u);
    });
  }

  // Creates the patient's Pre-Alignment project immediately, skipping the modal:
  // the patient is always the current one and the start date is always today.
  // Stays on the patient page (reload projects + lifecycle) instead of navigating
  // to the new project page.
  function createPreAlignmentProject(lifecycleAction) {
    const template = settings.projectTemplates.find(t =>
      lifecycleAction.group.templates.includes(t.id)
    );
    if (!template) return;
    const now = new Date();
    const startDate = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    submitCreateProject({
      templateID: template.id,
      patientID: patient.id,
      createdAt: startDate,
      onSuccess: () => {
        reloadProjects();
        axios.post('/api/admin/patients', {
          filter: { id: [patientID] },
          includeMissingRequired: true,
        }).then((response) => {
          if (response.data.patients && response.data.patients.length > 0) {
            setPatient(response.data.patients[0]);
          } else {
            setPatient(prev => ({ ...prev, lifecycleStage: 'PRE_ALIGNMENT' }));
          }
        }).catch(() => {
          setPatient(prev => ({ ...prev, lifecycleStage: 'PRE_ALIGNMENT' }));
        });
        switchTab('preAlignment');
      },
    });
  }

  function renderNoProjectsMessage() {
    const lifecycleAction = getLifecycleAction();
    return (
      <div className="d-flex flex-column justify-content-center align-items-center mt-4 gap-2">
        <div className="typo-normal-150 text-muted">No projects</div>
        {lifecycleAction && lifecycleAction.action === 'beginPre' && (
          <Button onClick={() => createPreAlignmentProject(lifecycleAction)} color="blue" text="Create Pre-Alignment Project" />
        )}
      </div>
    );
  }

  // Pre-Alignment step content: the pre-alignment project's tasks inline, or an empty
  // state offering to create the pre-alignment project.
  function renderPreAlignment() {
    const project = getPreAlignmentProject();
    if (!project) {
      return renderNoProjectsMessage();
    }
    const readOnly =
      patient.lifecycleStage === "POST_ALIGNMENT_NEW" ||
      patient.lifecycleStage === "POST_ALIGNMENT_EST";
    return (
      <ProjectTasks
        key={project.id}
        projectID={project.id}
        project={project}
        patient={patient}
        settings={settings}
        users={users}
        onProjectChange={reloadProjects}
        onPatientChange={setPatient}
        hasCognitiveStaging={hasCognitiveStaging}
        onCognitiveStagingChange={handleCognitiveStagingChange}
        readOnly={readOnly}
      />
    );
  }

  // Post-Alignment step content. Locked until a Post-Alignment project exists or the
  // patient is ready to start one (see canStartPostAlignment); then shows the
  // post-alignment project's tasks inline.
  function renderPostAlignment() {
    if (!getPostAlignmentProject() && !canStartPostAlignment()) {
      return (
        <div className="d-flex flex-column justify-content-center align-items-center mt-4 gap-2">
          <Lock height={20} width={20} color={'grey'} />
          <div className="typo-normal-150 text-muted">Assign a tier and set an Alignment Date, then start Post-Alignment from the tab above.</div>
        </div>
      );
    }

    const project = getPostAlignmentProject();
    if (!project) {
      return (
        <div className="d-flex flex-column justify-content-center align-items-center mt-4 gap-2">
          <div className="typo-normal-150 text-muted">No Post-Alignment project yet</div>
        </div>
      );
    }

    return (
      <ProjectTasks
        key={project.id}
        projectID={project.id}
        project={project}
        patient={patient}
        settings={settings}
        users={users}
        onProjectChange={reloadProjects}
        onPatientChange={setPatient}
        hasCognitiveStaging={hasCognitiveStaging}
        onCognitiveStagingChange={handleCognitiveStagingChange}
      />
    );
  }

  function createProjectOnClick(template) {
    setCreateProject({
      visible: true,
      template: template,
      patientID: patient.id,
    })
    setCloseCreateProjectDrop(closeCreateProjectDrop + 1);
  }

  function createProjectPatientUpdate(patient, id) {
    setCreateProject({ ...createProject,
      patientID: patient.id,
    });
  }

  function createProjectDateUpdate(date) {
    setCreateProject({ ...createProject,
      createdAt: date,
    });
  }

  function createProjectConfirm() {
    submitCreateProject({
      templateID: createProject.template.id,
      patientID: createProject.patientID,
      createdAt: createProject.createdAt,
      onSuccess: () => {
        reloadProjects();
        closeCreateProjectModal();
      },
    });
  }

  function closeCreateProjectModal() {
    setCreateProjectGroup(null);
    setCreateProject({ visible: false });
  }

  // Returns the lifecycle phase ("PRE", "POST_NEW", "POST_EST" or null) of a project
  // based on its template's group.
  function phaseForProject(project) {
    const group = (settings.projectTemplatesGroups || []).find(g => g.templates.includes(project.templateID));
    return group ? group.phase : null;
  }

  // Human-readable description of what deleting the project reverts the patient to.
  function revertLabelForProject(project) {
    switch (phaseForProject(project)) {
      case 'PRE':
        return 'This will remove the patient from the GUIDE lifecycle.';
      case 'POST_NEW':
        return 'The patient will revert to the Pre-Alignment stage and their alignment date will be cleared.';
      case 'POST_EST':
        return 'The patient will revert to the Post-Alignment (New) stage and this project and its tasks will be removed.';
      default:
        return null;
    }
  }

  // The patient's active GUIDE lifecycle project (the one whose cancellation unaligns them).
  function getActiveLifecycleProject() {
    const stageToPhase = {
      PRE_ALIGNMENT: 'PRE',
      POST_ALIGNMENT_NEW: 'POST_NEW',
      POST_ALIGNMENT_EST: 'POST_EST',
    };
    const active = (projects || []).filter(p =>
      phaseForProject(p) && p.statusType !== 'COMPLETED' && p.statusType !== 'CANCELED'
    );
    const wanted = stageToPhase[patient?.lifecycleStage];
    return active.find(p => phaseForProject(p) === wanted) || active[0] || null;
  }

  // The patient's Pre-Alignment project (phase PRE). Prefers an active one, falling back to
  // the most recent completed one so a finished pre-alignment project stays viewable.
  function getPreAlignmentProject() {
    const pre = (projects || []).filter(p => phaseForProject(p) === 'PRE' && p.statusType !== 'CANCELED');
    if (pre.length === 0) return null;
    return pre.find(p => p.statusType !== 'COMPLETED') || pre[pre.length - 1];
  }

  // The patient's Post-Alignment project (phase POST_NEW or POST_EST). Prefers an active one.
  function getPostAlignmentProject() {
    const post = (projects || []).filter(p => ['POST_NEW', 'POST_EST'].includes(phaseForProject(p)) && p.statusType !== 'CANCELED');
    if (post.length === 0) return null;
    return post.find(p => p.statusType !== 'COMPLETED') || post[post.length - 1];
  }

  // Whether the patient is ready for the user to explicitly start Post-Alignment (New):
  // a tier has been assigned (patient.procedureCode, set by the Tier Assignment task
  // action) and an Alignment Date is set, and there's no Post-Alignment project yet.
  // Confirming the "Start Post-Alignment" dialog completes/locks the Pre-Alignment
  // project as part of the transition — it doesn't need to already be completed.
  function canStartPostAlignment() {
    return patient?.statusType !== 'ARCHIVED'
      && !getPostAlignmentProject()
      && patient?.lifecycleStage === 'PRE_ALIGNMENT'
      && !!patient?.procedureCode
      && !!patient?.alignmentDate;
  }

  useEffect(() => {
    if (!unalignModal.visible) return;
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = ""; };
  }, [unalignModal.visible]);

  function openUnalignModal() {
    setClosePatientStatusDrop(closePatientStatusDrop + 1);

    const project = getActiveLifecycleProject();
    if (!project) return;

    const template = (settings.projectTemplates || []).find(t => t.id === project.templateID);
    const status = template ? template.statuses.find(s => s.type === 'CANCELED') : null;
    if (!status) {
      context.addError({ message: "No cancel status configured for this project's template." });
      return;
    }

    setUnalignModal({ visible: true, project, status, submitting: false, date: formatDateYMD(new Date()), reasonID: null });
  }

  function closeUnalignModal() {
    if (unalignModal.submitting) return;
    setUnalignModal({ visible: false, project: null, status: null, submitting: false, date: '', reasonID: null });
  }

  function submitUnalign() {
    const { project, status, date, reasonID } = unalignModal;
    if (!project || !status || !date) return;
    const hasReasons = (status.reasons || []).length > 0;
    if (hasReasons && !reasonID) return;
    setUnalignModal(prev => ({ ...prev, submitting: true }));
    let u = context.addSpiner();
    axios.post('/api/admin/projects/updateStatus', {
      id: project.id,
      statusID: status.id,
      reasonID: reasonID,
      unalignmentDate: date,
    }).then((response) => {
      setPatient(prev => ({
        ...prev,
        statusType: 'CANCELED',
        lifecycleStage: 'UNALIGNED',
        unalignmentDate: response.data.unalignmentDate || date,
      }));
      setProjects(prev => prev.map(p => p.id === project.id ? { ...p,
        statusType: response.data.statusType,
        statusID: response.data.statusID,
        statusName: response.data.statusName,
        statusReasonID: response.data.statusReasonID,
        statusReasonName: response.data.statusReasonName,
        closedAt: response.data.closedAt,
      } : p));
      setUnalignModal({ visible: false, project: null, status: null, submitting: false, date: '', reasonID: null });
    }).catch((e) => {
      context.addError(e);
      setUnalignModal(prev => ({ ...prev, submitting: false }));
      context.removeSpiner(u);
    }).finally(() => {
      context.removeSpiner(u);
    });
  }

  function openDeleteProjectModal(project) {
    setDeleteProject({ visible: true, project });
  }

  function closeDeleteProjectModal() {
    setDeleteProject({ visible: false, project: null });
  }

  function openDeleteCaregiverModal(caregiver) {
    setDeleteCaregiver({ visible: true, caregiver });
  }

  function closeDeleteCaregiverModal() {
    setDeleteCaregiver({ visible: false, caregiver: null });
  }

  function deleteCaregiverConfirm() {
    const caregiver = deleteCaregiver.caregiver;
    if (!caregiver || !patient) return;

    let u = context.addSpiner();
    axios.post('/api/admin/patients/deleteCaregiver', {
      patientID: patient.id,
      id: caregiver.id,
    }).then(() => {
      setPatient(prev => {
        const caregivers = (prev.caregivers || []).filter(c => c.id !== caregiver.id);
        return {
          ...prev,
          caregivers,
          hasCaregiver: caregivers.length === 0 ? null : prev.hasCaregiver,
        };
      });
      closeDeleteCaregiverModal();
    }).catch((e) => {
      context.addError(e);
      context.removeSpiner(u);
    }).finally(() => {
      context.removeSpiner(u);
    });
  }

  function deleteProjectConfirm() {
    const project = deleteProject.project;
    if (!project) return;

    let u = context.addSpiner();
    axios.post('/api/admin/projects/delete', {
      id: project.id,
    }).then(() => {
      // Remove the project locally.
      setProjects(prev => prev.filter(p => p.id !== project.id));

      // Reflect the patient's lifecycle revert locally so the UI stays consistent.
      const phase = phaseForProject(project);
      if (phase === 'PRE') {
        setPatient(prev => ({ ...prev, lifecycleStage: '' }));
      } else if (phase === 'POST_NEW') {
        setPatient(prev => ({ ...prev, lifecycleStage: 'PRE_ALIGNMENT', alignmentDate: null }));
      } else if (phase === 'POST_EST') {
        setPatient(prev => ({ ...prev, lifecycleStage: 'POST_ALIGNMENT_NEW' }));
      }

      closeDeleteProjectModal();
    }).catch((e) => {
      context.addError(e);
      context.removeSpiner(u);
    }).finally(() => {
      context.removeSpiner(u);
    });
  }

  // Returns the next lifecycle action the patient is eligible for, if any.
  // { action: 'beginPre', group, requiresAlignmentDate }
  // Starting Post-Alignment (New) is not part of this — see canStartPostAlignment
  // and confirmStartPostAlignment for that explicitly-confirmed transition.
  // New → Est conversion is automatic at program month 7 (see ensureEstConversion).
  function getLifecycleAction() {
    if (!patient || !settings || !settings.projectTemplatesGroups) return null;
    const stage = patient.lifecycleStage;

    if (!stage) {
      // Patient has never entered the GUIDE lifecycle.
      const preGroup = settings.projectTemplatesGroups.find(g => g.phase === 'PRE');
      if (!preGroup) return null;
      return { action: 'beginPre', group: preGroup, requiresAlignmentDate: false };
    }

    return null;
  }

  function dueForEstConversion() {
    if (!patient || patient.lifecycleStage !== 'POST_ALIGNMENT_NEW' || !patient.alignmentDate) {
      return false;
    }
    return programMonths(DatetimeToDate(patient.alignmentDate)) >= 7;
  }

  function ensureEstConversion() {
    if (!patient || estConvertAttempted.current) return;
    if (!dueForEstConversion() && !patient.estGcodeNoticePending) return;

    estConvertAttempted.current = true;
    axios.post('/api/admin/projects/convertToEst', {
      patientID: patient.id,
    }).then((response) => {
      const data = response.data || {};
      if (data.converted) {
        reloadProjects();
        axios.post('/api/admin/patients', {
          filter: { id: [patientID] },
          includeMissingRequired: true,
        }).then((patientsResponse) => {
          if (patientsResponse.data.patients && patientsResponse.data.patients.length > 0) {
            setPatient(patientsResponse.data.patients[0]);
          } else {
            setPatient(prev => ({
              ...prev,
              lifecycleStage: 'POST_ALIGNMENT_EST',
              procedureCode: data.to || prev.procedureCode,
              estGcodeNoticePending: true,
              estGcodeNoticeFrom: data.from || '',
              estGcodeNoticeTo: data.to || '',
            }));
          }
        }).catch(() => {
          setPatient(prev => ({
            ...prev,
            lifecycleStage: 'POST_ALIGNMENT_EST',
            procedureCode: data.to || prev.procedureCode,
            estGcodeNoticePending: true,
            estGcodeNoticeFrom: data.from || '',
            estGcodeNoticeTo: data.to || '',
          }));
        });
      }
      if (data.converted || data.noticePending) {
        setEstModal({
          visible: true,
          from: data.from || '',
          to: data.to || '',
          submitting: false,
        });
      }
    }).catch((e) => {
      // Allow retry on next load if conversion failed (e.g. unmapped code).
      estConvertAttempted.current = false;
      context.addError(e);
    });
  }

  function closeEstModal() {
    if (estModal.submitting) return;
    setEstModal(prev => ({ ...prev, submitting: true }));
    axios.post('/api/admin/patients/acknowledgeEstGcodeNotice', {
      patientID: patient.id,
    }).then(() => {
      setPatient(prev => ({
        ...prev,
        estGcodeNoticePending: false,
        estGcodeNoticeFrom: '',
        estGcodeNoticeTo: '',
      }));
      setEstModal({ visible: false, from: '', to: '', submitting: false });
    }).catch((e) => {
      context.addError(e);
      setEstModal(prev => ({ ...prev, submitting: false }));
    });
  }

  const PHASE_STEPS = ["profile", "caregivers", "preAlignment", "postAlignment"];

  function isPhasesTab() {
    return PHASE_STEPS.includes(actionTabFilter);
  }

  // The workflow step the Phases tab lands on, derived from lifecycle stage
  // (reuses the same default logic used when the page first loads).
  function defaultPhaseStep() {
    const stage = patient?.lifecycleStage;
    if (stage === 'POST_ALIGNMENT_NEW' || stage === 'POST_ALIGNMENT_EST') return 'postAlignment';
    // Unaligned patients have both alignment steps locked (see the `unaligned`
    // check above), so default to Patient Info instead of a locked step.
    if (stage === 'UNALIGNED') return 'profile';
    return 'preAlignment';
  }

  function openStartPostAlignmentModal() {
    setStartPostAlignmentModal({ visible: true, submitting: false, confirmed: false });
  }

  function closeStartPostAlignmentModal() {
    setStartPostAlignmentModal({ visible: false, submitting: false, confirmed: false });
  }

  // Confirms the explicit "Start Post-Alignment" transition: completes/locks the
  // open Pre-Alignment project and creates the Post-Alignment project from the
  // tier already assigned (server resolves New vs Est from patient.procedureCode
  // + patient.alignmentDate). Reloads projects, refetches the patient for the
  // new lifecycleStage, and switches to the Post-Alignment tab.
  function confirmStartPostAlignment() {
    if (!startPostAlignmentModal.confirmed || startPostAlignmentModal.submitting) return;
    setStartPostAlignmentModal(prev => ({ ...prev, submitting: true }));
    axios.post('/api/admin/projects/startPostAlignmentNew', {
      patientID: patient.id,
    }).then(() => {
      closeStartPostAlignmentModal();
      reloadProjects();
      axios.post('/api/admin/patients', {
        filter: { id: [patientID] },
        includeMissingRequired: true,
      }).then((response) => {
        if (response.data.patients && response.data.patients.length > 0) {
          setPatient(response.data.patients[0]);
        }
      }).catch((e) => {
        context.addError(e);
      });
      switchTab('postAlignment');
    }).catch((e) => {
      context.addError(e);
      setStartPostAlignmentModal(prev => ({ ...prev, submitting: false }));
    });
  }

  // Appointments
  function setAppointmentsSortOrderAsc() {
    setAppointmentsSorter(prev => ({ ...prev, order: "asc" }));
    setCloseAppointmentSorterDrop(prev => prev + 1);
  }

  function setAppointmentsSortOrderDesc() {
    setAppointmentsSorter(prev => ({ ...prev, order: "desc" }));
    setCloseAppointmentSorterDrop(prev => prev + 1);
  }

  function toggleAppointmentStatusFilter(status) {
    setAppointmentsFilter(prev => {
      const currentStatuses = prev.statusType || [];
      if (currentStatuses.includes(status)) {
        // Don't allow deselecting if it's the last selected status
        if (currentStatuses.length === 1) {
          return prev;
        }
        return { ...prev, statusType: currentStatuses.filter(s => s !== status) };
      } else {
        return { ...prev, statusType: [...currentStatuses, status] };
      }
    });
  }

  function setAppointmentUserFilterAll() {
    setAppointmentsFilter(prev => ({ ...prev, userID: [] }));
    setCloseAppointmentUserFilterDrop(prev => prev + 1);
  }

  function setAppointmentUserFilterMe() {
    setAppointmentsFilter(prev => ({ ...prev, userID: [context.id] }));
    setCloseAppointmentUserFilterDrop(prev => prev + 1);
  }

  function renderAppointments() {
    if (!appointments || appointments.length === 0) {
      return (
        <div className="d-flex justify-content-center align-items-center mt-4">
          <div className="typo-normal-150 text-muted">No appointments</div>
        </div>
      );
    }

    return appointments.map((appointment) => {
      return (
        <AppointmentCard
          key={appointment.id}
          appointment={appointment}
          users={users}
          settings={settings}
        />
      );
    });
  }

  function createAppointmentOnClick(template) {
    setCreateAppointment({
      visible: true,
      template: template,
      userID: null,
    })
    setCloseCreateAppointmentDrop(prev => prev + 1);
  }

  function createAppointmentUserUpdate(user, id) {
    setCreateAppointment(prev => ({ ...prev,
      userID: user.id,
    }));
  }

  function createAppointmentTitleUpdate(text) {
    setCreateAppointment(prev => ({ ...prev,
      title: text,
    }));
  }

  function createAppointmentDescriptionUpdate(text, id) {
    setCreateAppointment(prev => ({ ...prev,
      description: text,
    }));
  }

  function createAppointmentConfirm() {
    let u = context.addSpiner();
    axios.post('/api/admin/appointments/create', {
      patientID: patient.id,
      templateID: createAppointment.template.id,
      userID: createAppointment.userID,
      title: createAppointment.title,
      description: createAppointment.description,
    }).then((response) => {
      setCreateAppointment({ visible: false });
      setAppointments(prevAppointments => [...prevAppointments, response.data]);
      setCloseCreateAppointmentDrop(prev => prev + 1);
    }).catch((e) => {
      context.addError(e);
      context.removeSpiner(u);
    }).finally(() => {
      context.removeSpiner(u);
    });
  }

  // Assessments
  function setAssessmentsSortOrderAsc() {
    setAssessmentsSorter(prev => ({ ...prev, order: "asc" }));
    setCloseAssessmentSorterDrop(prev => prev + 1);
  }

  function setAssessmentsSortOrderDesc() {
    setAssessmentsSorter(prev => ({ ...prev, order: "desc" }));
    setCloseAssessmentSorterDrop(prev => prev + 1);
  }

  function toggleStatusFilter(status) {
    setAssessmentsFilter(prev => {
      const currentStatuses = prev.statusType || [];
      if (currentStatuses.includes(status)) {
        // Don't allow deselecting if it's the last selected status
        if (currentStatuses.length === 1) {
          return prev;
        }
        return { ...prev, statusType: currentStatuses.filter(s => s !== status) };
      } else {
        return { ...prev, statusType: [...currentStatuses, status] };
      }
    });
  }

  function setUserFilterAll() {
    setAssessmentsFilter(prev => ({ ...prev, userID: [] }));
    setCloseAssessmentUserFilterDrop(prev => prev + 1);
  }

  function setUserFilterMe() {
    setAssessmentsFilter(prev => ({ ...prev, userID: [context.id] }));
    setCloseAssessmentUserFilterDrop(prev => prev + 1);
  }

  function renderAssessments() {
    if (!assessments || assessments.length === 0) {
      return (
        <div className="d-flex justify-content-center align-items-center mt-4">
          <div className="typo-normal-150 text-muted">No assessments</div>
        </div>
      );
    }

    return assessments.map((assessment) => {
      return (
        <AssessmentCard
          key={assessment.id}
          assessment={assessment}
          users={users}
          settings={settings}
          onFormClick={(clicked) => {
            if (isCognitiveStagingTemplate(clicked.templateID)) {
              setCognitiveStagingForm({ visible: true, template: null, assessmentID: clicked.id });
              return;
            }
            navigate('/assessment/' + clicked.id);
          }}
          onStatusChange={reloadCognitiveStagingStatus}
        />
      );
    });
  }

  function createAssessmentOnClick(template) {
    setCloseCreateAssessmentDrop(prev => prev + 1);
    if (isCognitiveStagingTemplate(template.id)) {
      setCognitiveStagingForm({ visible: true, template, assessmentID: null });
      return;
    }
    setCreateAssessment({
      visible: true,
      template: template,
      userID: null,
    });
  }

  function createAssessmentUserUpdate(user, id) {
    setCreateAssessment(prev => ({ ...prev, 
      userID: user.id, 
    }));
  }

  function createAssessmentConfirm() {
    let u = context.addSpiner();
    axios.post('/api/admin/assessments/create', {
      patientID: patient.id,
      templateID: createAssessment.template.id,
      userID: createAssessment.userID,
    }).then((response) => {
      setCreateAssessment({ visible: false });
      setAssessments(prevAssessments => [...prevAssessments, response.data]);
      reloadCognitiveStagingStatus();
      setCloseCreateAssessmentDrop(prev => prev + 1);
      //setScrollAssessmentID(response.data.id);
      //scrollTo("assessment", response.data.id);
    }).catch((e) => {
      context.addError(e);
      context.removeSpiner(u);
    }).finally(() => {
      context.removeSpiner(u);
    });
  }

  // Medications
  function renderMedications() {
    if (!medications || medications.length === 0) {
      return (
        <div className="d-flex justify-content-center align-items-center mt-4">
          <div className="typo-normal-150 text-muted">No medications</div>
        </div>
      );
    }

    return medications.map((medication) => {
      return (
        <div key={medication.id} className="card mb-2">
          <div className="card-content sm">
            <div className="d-flex justify-content-between align-items-start">
              <div className="w-100">
                <div className="typo-strong-175">{medication.name}</div>
                {medication.code && (
                  <div className="typo-normal-150 text-muted mt-1">Code: {medication.code}</div>
                )}
                <div className="typo-normal-150 mt-2">
                  {medication.dose && <span>{medication.dose}</span>}
                  {medication.dose && medication.frequency && <span> • </span>}
                  {medication.frequency && <span>{medication.frequency}</span>}
                  {medication.route && <span> • {medication.route}</span>}
                </div>
                {medication.sig && (
                  <div className="typo-normal-150 mt-1">{medication.sig}</div>
                )}
                {medication.indication && (
                  <div className="typo-normal-150 text-muted mt-1">Indication: {medication.indication}</div>
                )}
                <div className="d-flex justify-content-start align-items-center mt-2">
                  {medication.start && (
                    <div className="typo-normal-125 text-muted">Start: {DateToFormat(DatetimeToDate(medication.start), context.dateFormat)}</div>
                  )}
                  {medication.end && (
                    <>
                      {medication.start && <span className="typo-normal-125 text-muted ms-2">•</span>}
                      <div className="typo-normal-125 text-muted ms-2">End: {DateToFormat(DatetimeToDate(medication.end), context.dateFormat)}</div>
                    </>
                  )}
                </div>
                <div className="d-flex justify-content-start align-items-center mt-2">
                  <div className="typo-normal-125 text-muted">Status: {medication.status}</div>
                  {medication.source && (
                    <>
                      <span className="typo-normal-125 text-muted ms-2">•</span>
                      <div className="typo-normal-125 text-muted ms-2">Source: {medication.source}</div>
                    </>
                  )}
                </div>
                {medication.notes && (
                  <div className="typo-normal-150 mt-2">{medication.notes}</div>
                )}
                <div className="typo-normal-125 text-muted mt-2">
                  Prescribed by: {medication.userName || 'Unknown'}
                </div>
              </div>
            </div>
          </div>
        </div>
      );
    });
  }

  const [closeCreateMedicationDrop, setCloseCreateMedicationDrop] = useState(0);
  const [createMedication, setCreateMedication] = useState({
    visible: false,
    patientID: null,
    userID: null,
    name: '',
    code: '',
    dose: '',
    frequency: '',
    route: '',
    sig: '',
    indication: '',
    start: null,
    end: null,
    status: 'active',
    source: 'provider',
    notes: '',
  });

  function createMedicationOnClick() {
    setCreateMedication({
      visible: true,
      patientID: patient.id,
      userID: null,
      name: '',
      code: '',
      dose: '',
      frequency: '',
      route: '',
      sig: '',
      indication: '',
      start: null,
      end: null,
      status: 'active',
      source: 'provider',
      notes: '',
    });
    setCloseCreateMedicationDrop(closeCreateMedicationDrop + 1);
  }

  function createMedicationUserUpdate(user, id) {
    setCreateMedication({ ...createMedication, userID: user.id });
  }

  function createMedicationConfirm() {
    let u = context.addSpiner();
    axios.post('/api/admin/medications/create', {
      patientID: createMedication.patientID,
      userID: createMedication.userID,
      name: createMedication.name,
      code: createMedication.code,
      dose: createMedication.dose,
      frequency: createMedication.frequency,
      route: createMedication.route,
      sig: createMedication.sig,
      indication: createMedication.indication || null,
      start: createMedication.start,
      end: createMedication.end || null,
      status: createMedication.status,
      source: createMedication.source,
      notes: createMedication.notes,
    }).then((response) => {
      setMedications([...medications, response.data]);
      setCreateMedication({
        visible: false,
        patientID: null,
        userID: null,
        name: '',
        code: '',
        dose: '',
        frequency: '',
        route: '',
        sig: '',
        indication: '',
        start: null,
        end: null,
        status: 'active',
        source: 'provider',
        notes: '',
      });
      setCloseCreateMedicationDrop(closeCreateMedicationDrop + 1);
    }).catch((e) => {
      context.addError(e);
      context.removeSpiner(u);
    }).finally(() => {
      context.removeSpiner(u);
    });
  }

  // Render helpers
  function renderProjectsTemplates() {
    // No group selected - render the list of groups
    if (!createProjectGroup) {
      if (!settings.projectTemplatesGroups || settings.projectTemplatesGroups.length === 0) {
        return [];
      }
      
      return settings.projectTemplatesGroups.map((group) => {
        return (
          <div className="item" style={{minWidth: 260}} onClick={() => setCreateProjectGroup(group.id)}>
            <div className="w-100">
              <div className="typo-header-150">{group.title}</div>
              <div className="typo-normal-125">{group.description}</div>
            </div>
          </div>
        );
      });
    }

    // Group selected - filter project templates by the group's template IDs
    const templatesGroup = settings.projectTemplatesGroups.find((group) => group.id === createProjectGroup);
    
    if (!templatesGroup || !templatesGroup.templates) {
      return [];
    }

    const filteredTemplates = settings.projectTemplates.filter((template) => 
      templatesGroup.templates.includes(template.id)
    );

    if (filteredTemplates.length === 0) {
      return (
        <div className="item" style={{minWidth: 260}}>
          <div className="w-100">
            <div className="typo-header-150 text-muted">No templates available</div>
            <div className="typo-normal-125 text-muted">This group doesn't have any project templates yet</div>
          </div>
        </div>
      );
    }

    return filteredTemplates.map((template) => {
      return (
        <div className="item" style={{minWidth: 260}} onClick={() => createProjectOnClick(template)}>
          <div className="w-100">
            <div className="typo-header-150">{template.title}</div>
            <div className="typo-normal-125">{template.description}</div>
          </div>
        </div>
      );
    });
  }

  // Caregivers
  function renderCaregiverCards() {
    if (!patient || !patient.caregivers || patient.caregivers.length === 0) {
      return (
        <div className="d-flex justify-content-center align-items-center mt-4">
          <div className="typo-normal-150 text-muted">No caregivers</div>
        </div>
      );
    }

    return patient.caregivers.map((caregiver, i) => {
      let tempalte = settings.caregiverTemplate;

      let title = tempalte.highlightTitle;
      let description = tempalte.highlightDescription;

      // Find value in form by code
      if (tempalte.highlightTitle) {
        title = tempalte.highlightTitle.split(" ").map((code) => {
          let field = caregiver.fields.find((f) => f.code === code);
          return (field && field.answers[0]) ? field.answers[0] : "";
        }).join(" ");
      }

      if (tempalte.highlightDescription) {
        description = tempalte.highlightDescription.split(" ").map((code) => {
          let field = caregiver.fields.find((f) => f.code === code);
          return (field && field.answers[0]) ? field.answers[0] : "";
        }).join(" ");
      }

      return (
        <div key={caregiver.id || i}>
          <div className="d-flex justify-content-between align-items-center mb-2 px-1">
            <div className="d-flex flex-column justify-content-center">
              {title && <span className="typo-strong-175">{title}</span>}
              {description !== "" && <div className="typo-normal-150">{description}</div>}
            </div>
            <div className="d-flex align-items-center" style={{ gap: 8 }}>
              <div
                className="item typo-normal-150"
                onClick={() => navigate(`/caregiver/${caregiver.id}?patientID=${patient.id}`)}
              >
                <FileEdit height={16} width={16} color={'grey'} /> Edit
              </div>
              <div
                className="item typo-normal-150"
                onClick={() => openDeleteCaregiverModal(caregiver)}
              >
                <Delete height={16} width={16} color={'grey'} /> Delete
              </div>
            </div>
          </div>
          {renderCaregiverPages(caregiver, i)}
        </div>
      );
    });
  }

  function renderCaregivers() {
    const hasCaregiver = patient?.hasCaregiver;
    const hasRecords = !!(patient?.caregivers?.length);

    if (hasRecords) {
      return renderCaregiverCards();
    }

    return (
      <div className="card mb-2">
        <div className="card-content sm">
          <div className="typo-strong-175 mb-2">Does this patient have a caregiver?</div>
          <div className="d-flex align-items-center" style={{ gap: 8 }}>
            <div
              className={"item typo-normal-150 " + (hasCaregiver === true ? "active" : "")}
              onClick={() => updateHasCaregiver(true)}
            >
              Yes
            </div>
            <div
              className={"item typo-normal-150 " + (hasCaregiver === false ? "active" : "")}
              onClick={() => updateHasCaregiver(false)}
            >
              No
            </div>
          </div>
          {hasCaregiver === false && (
            <div className="typo-normal-150 text-muted mt-3">No caregiver on file</div>
          )}
          {hasCaregiver == null && (
            <div className="typo-normal-150 text-muted mt-3">Select Yes or No to continue</div>
          )}
          {hasCaregiver === true && (
            <div
              className="item typo-normal-150 mt-3"
              onClick={() => navigate('/caregiver/create?patientID=' + patient.id)}
            >
              <FilePlus height={16} width={16} color={'grey'} /> Create Caregiver
            </div>
          )}
        </div>
      </div>
    );
  }

  function renderCaregiverPages(caregiver, caregiverIndex) {
    // PAAF export only uses the primary caregiver (Caregivers[0]).
    const answers = caregiverAnswers(caregiver);
    const checkMissing = caregiverIndex === 0
      ? (code) => isCaregiverFieldMissing(code, answers)
      : undefined;

    return caregiver.pages.map((page) => {
      return (
        <div key={`${caregiver.id || caregiverIndex}_${page.id}`} className="card mb-2">
          <div className="card-header sm">
            <div className="typo-strong-175">{page.title}</div>
          </div>
          <div className="card-content sm">
            {page.lines.map((line, index) => renderFormLine(page.id, line, index, page.lines.length === index + 1, settings.caregiverTemplate.fields, caregiver.fields, context.dateFormat, checkMissing))}
          </div>
        </div>
      );
    });
  }

  // Care Plan
  function renderCarePlan() {
    if (!plans || plans.length === 0) {
      return (
        <div className="d-flex justify-content-center align-items-center mt-4">
          <div className="typo-normal-150 text-muted">No care plans</div>
        </div>
      );
    }

    const selectedPlan = plans.find(p => p.id === selectedPlanID);
    if (!selectedPlan) {
      return (
        <div className="d-flex justify-content-center align-items-center mt-4">
          <div className="typo-normal-150 text-muted">Select a care plan</div>
        </div>
      );
    }

    // Find the plan template from settings
    const planTemplate = settings?.planTemplates?.find(t => t.id === selectedPlan.templateID);

    return (
      <>
        {/* Plan Selector */}
        {plans.length > 1 && (
          <div className="row mb-2">
            {plans.map(p => (
              <div key={p.id} className="col-xs-3">
                <div 
                  className={"card" + (selectedPlanID === p.id ? " blue" : "")} 
                  onClick={() => setSelectedPlanID(p.id)}
                  style={{cursor: 'pointer'}}
                >
                  <div className="card-header sm">
                    <div className="typo-strong-150">{p.templateName}</div>
                    <div className="typo-normal-125 text-muted">{DateToFormat(DatetimeToDate(p.createdAt), context.dateFormat)}</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Plan Header */}
        <div className="card mb-2">
          <div className="card-header sm">
            <div className="d-flex justify-content-between align-items-top">
              <div>
                <div className="typo-strong-200">{selectedPlan.templateName}</div>
                <div className="typo-normal-150 text-muted">
                  Created {DateToFormat(DatetimeToDate(selectedPlan.createdAt), context.dateFormat)} by {selectedPlan.createdByName}
                </div>
              </div>
              <div className="d-flex justify-content-end align-items-top">
                {selectedPlan.closedAt ? (
                  <Status color={"grey"} muted={true} text={"Closed"} />
                ) : (
                  <Status color={"green"} muted={true} text={"Active"} />
                )}
                {!selectedPlan.closedAt && (
                  <Drop close={closePlanDrop} side={"right"} className="ms-1">
                    <div className="item rect">
                      <MoreVertical height={14} width={16} color={'grey'} />
                    </div>
                    <div className="card" style={{width: 200}}>
                      <div className="card-header sm">
                        <div className="side-item small typo-medium-150" onClick={closePlanConfirm}>
                          <div className="typo-normal-150">Close Plan</div>
                        </div>
                      </div>
                    </div>
                  </Drop>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Plan Sections */}
        {selectedPlan.sections && selectedPlan.sections.map((section, si) => (
          <PlanSectionCard
            key={"care_plan_section_" + selectedPlan.id + "_" + si}
            section={section}
            planID={selectedPlan.id}
            planTemplate={planTemplate}
            users={users}
            onNoteAdded={() => handleSectionNoteAdded(selectedPlan.id, section.id)}
          />
        ))}
      </>
    );
  }

  // Respite (accounts) — patient-wide transactions grouped by performance year
  const respiteData = useMemo(() => {
    if (!accounts || accounts.length === 0) {
      return { groups: [], yearOptions: [], hasAny: false };
    }

    const allTxns = [];
    accounts.forEach((account) => {
      (account.transactions || []).forEach((t) => {
        allTxns.push({
          ...t,
          accountID: account.id,
          accountTemplateName: account.templateName,
        });
      });
    });

    const groups = groupByPerformanceYear(allTxns);

    const currentPY = performanceYearOf(new Date());
    const yearMap = new Map();
    yearMap.set(currentPY.startYear, { startYear: currentPY.startYear, label: currentPY.label });
    groups.forEach((g) => {
      if (!yearMap.has(g.startYear)) {
        yearMap.set(g.startYear, { startYear: g.startYear, label: g.label });
      }
    });
    const yearOptions = Array.from(yearMap.values()).sort((a, b) => b.startYear - a.startYear);

    return { groups, yearOptions, hasAny: allTxns.length > 0 };
  }, [accounts]);

  function reloadAccounts() {
    return axios.post('/api/admin/accounts', {
      filter: { userID: [patientID] },
    }).then((response) => {
      setAccounts(response.data.accounts);
    });
  }

  function deleteRespiteTransaction(accountID, transactionID) {
    if (!window.confirm("Are you sure you want to delete this transaction and refund the amount to the account balance?")) {
      return;
    }

    let u = context.addSpiner();
    axios.post('/api/admin/accounts/transactions/delete', {
      accountID: accountID,
      transactionID: transactionID,
    }).then(() => reloadAccounts()).catch((e) => {
      context.addError(e);
    }).finally(() => {
      context.removeSpiner(u);
    });
  }

  function serviceOnModal() {
    const serviceProviders = partners.filter(p => p.serviceProvider === true);

    if (!serviceProviders || serviceProviders.length === 0) {
      context.addError({ response: { data: { error: 'No service providers available' } } });
      return;
    }

    const firstPartner = serviceProviders[0];
    const activeServices = firstPartner?.services?.filter(s => s.status !== false) || [];

    if (!activeServices || activeServices.length === 0) {
      context.addError({ response: { data: { error: 'No active services available for this partner' } } });
      return;
    }

    setLogDatesVisible(false);
    setCreateService({
      visible: true,
      account: accounts[0],
      partner: firstPartner,
      service: activeServices[0],
      dates: [new Date()],
    });
  }

  function serviceUpdateAccount(account) {
    setCreateService(prev => ({
      ...prev,
      account: account,
    }));
  }

  function serviceUpdatePartner(partner) {
    const activeServices = partner?.services?.filter(s => s.status !== false) || [];

    setCreateService(prev => ({
      ...prev,
      partner: partner,
      service: activeServices[0] || null,
    }));
  }

  function serviceUpdateService(service) {
    setCreateService(prev => ({
      ...prev,
      service: service,
    }));
  }

  function sameCalendarDay(a, b) {
    return a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
  }

  function serviceLogDate(date) {
    setCreateService(prev => {
      const alreadyLogged = (prev.dates || []).some((d) => sameCalendarDay(d, date));
      if (alreadyLogged) {
        return prev;
      }
      return {
        ...prev,
        dates: [...(prev.dates || []), date],
      };
    });
    setLogDatesVisible(false);
  }

  function serviceRemoveDate(index) {
    setCreateService(prev => {
      if (!prev.dates || prev.dates.length <= 1) {
        return prev;
      }
      return {
        ...prev,
        dates: prev.dates.filter((_, i) => i !== index),
      };
    });
  }

  function serviceCreate() {
    if (!createService.partner || !createService.service) {
      return;
    }

    if (!createService.dates || createService.dates.length === 0) {
      context.addError({ response: { data: { error: 'At least one date of service is required' } } });
      return;
    }

    let u = context.addSpiner();
    axios.post('/api/admin/accounts/transactions/create', {
      accountID: createService.account.id,
      partnerID: createService.partner.id,
      serviceID: createService.service.id,
      dates: createService.dates,
    }).then(() => reloadAccounts()).then(() => {
      setLogDatesVisible(false);
      setCreateService({
        visible: false,
        partner: null,
        service: null,
        account: null,
      });
    }).catch((e) => {
      context.addError(e);
      context.removeSpiner(u);
    }).finally(() => {
      context.removeSpiner(u);
    });
  }

  // Accounts
  function renderAccounts() {
    const { groups } = respiteData;

    // Union of years from transactions + configured caps, newest first
    const capYears = (settings && settings.respiteCaps || []).map((c) => ({
      startYear: c.startYear,
      label: `${c.startYear}–${c.startYear + 1}`,
    }));
    const allYearMap = new Map();
    allYearMap.set(currentRespitePYStart, {
      startYear: currentRespitePYStart,
      label: `${currentRespitePYStart}–${currentRespitePYStart + 1}`,
    });
    groups.forEach((g) => { if (!allYearMap.has(g.startYear)) allYearMap.set(g.startYear, { startYear: g.startYear, label: g.label }); });
    capYears.forEach((y) => { if (!allYearMap.has(y.startYear)) allYearMap.set(y.startYear, y); });
    const allYears = Array.from(allYearMap.values()).sort((a, b) => b.startYear - a.startYear);

    function toggleYear(startYear) {
      setExpandedRespiteYears((prev) =>
        prev.includes(startYear) ? prev.filter((y) => y !== startYear) : [...prev, startYear]
      );
    }

    return (
      <>
        {allYears.map((y) => {
          const grp = groups.find((g) => g.startYear === y.startYear);
          const spent = grp ? grp.subtotal : 0;
          const txns = grp ? grp.txns : [];
          const cap = capForYear(settings && settings.respiteCaps, y.startYear);
          const balance = cap != null ? cap - spent : null;
          const overCap = balance != null && balance < 0;
          const lowBalance = balance != null && balance >= 0 && balance < 700;
          const isExpanded = expandedRespiteYears.includes(y.startYear);

          return (
            <div key={"py_acc_" + y.startYear} className="card mb-2">
              <div
                className="card-header sm"
                style={{ cursor: 'pointer' }}
                onClick={() => toggleYear(y.startYear)}
              >
                <div className="d-flex justify-content-between align-items-center">
                  <div className="typo-strong-175">PY {y.label}</div>
                  <div className="d-flex align-items-center" style={{ gap: '16px' }}>
                    {balance != null ? (
                      <div className="typo-normal-150 d-flex align-items-center" style={{ color: overCap ? 'firebrick' : (lowBalance ? 'orange' : 'inherit'), gap: '4px' }}>
                        {lowBalance && <Alert height={14} width={14} color={'orange'} />}
                        Balance: {overCap ? '-' : ''}${Math.abs(balance).toFixed(2)}
                        {overCap && <span className="ms-1" style={{ color: 'firebrick' }}>(over cap)</span>}
                      </div>
                    ) : (
                      <div className="typo-normal-150 text-muted">
                        Spent: ${spent.toFixed(2)} <span style={{ fontStyle: 'italic' }}>· No cap set</span>
                      </div>
                    )}
                    {isExpanded ? <ChevronUp height={14} width={14} color={'grey'} /> : <ChevronDown height={14} width={14} color={'grey'} />}
                  </div>
                </div>
              </div>

              {isExpanded && (
                <div className="card-content sm">
                  {cap != null && (
                    <div className="d-flex align-items-center mb-2" style={{ gap: '16px' }}>
                      <div className="typo-normal-125 text-muted">Used ${spent.toFixed(2)} of ${cap.toFixed(2)} annual benefit</div>
                    </div>
                  )}
                  <div className="row typo-strong-150 mb-1 pb-1 border-bottom">
                    <div className="col-xs-2">Date</div>
                    <div className="col-xs-3">Title</div>
                    <div className="col-xs-3">Partner</div>
                    <div className="col-xs-2">Project</div>
                    <div className="col-xs-1 text-end">Amount</div>
                    <div className="col-xs-1"></div>
                  </div>
                  {txns.length === 0 ? (
                    <div className="typo-normal-150 text-muted text-center mt-2">No respite transactions recorded</div>
                  ) : (
                    txns.map((t, i) => {
                      const txnProject = t.projectID ? projects.find((p) => p.id === t.projectID) : null;
                      return (
                        <div key={"resp_" + y.startYear + "_" + i} className="row align-items-center mb-1 pb-1 border-bottom">
                          <div className="col-xs-2 typo-normal-150">{DateToFormat(DatetimeToDate(t.date), context.dateFormat)}</div>
                          <div className="col-xs-3 typo-normal-150">{t.title}</div>
                          <div className="col-xs-3 typo-normal-150">{t.partnerName}</div>
                          <div className="col-xs-2 typo-normal-150">{txnProject ? txnProject.templateName : <span className="text-muted">—</span>}</div>
                          <div className="col-xs-1 typo-strong-200 text-end">-${Number(t.amount).toFixed(2)}</div>
                          <div className="col-xs-1 text-end">
                            <div className="item" onClick={(e) => { e.stopPropagation(); deleteRespiteTransaction(t.accountID, t.id); }}>
                              <Delete height={16} width={16} color={'grey'} />
                            </div>
                          </div>
                        </div>
                      );
                    })
                  )}
                </div>
              )}
            </div>
          );
        })}
      </>
    );
  }


  function updatePatientUser(user) {
    let u = context.addSpiner();
    axios.post('/api/admin/patients/updateUser', {
      id: patient.id,
      userID: user.id,
    }).then(() => {
      setPatient(prev => ({ ...prev, userID: user.id, userName: user.firstName + (user.middleName ? ' ' + user.middleName : '') + ' ' + user.lastName }));
      setProjects(prev => prev.map(p => ({ ...p, userID: user.id, userName: user.firstName + (user.middleName ? ' ' + user.middleName : '') + ' ' + user.lastName })));
    }).catch((e) => {
      context.addError(e);
      context.removeSpiner(u);
    }).finally(() => {
      context.removeSpiner(u);
    });
  }

  // Update referral source
  function openUpdateReferralSourceModal() {
    setUpdateReferralSource({
      visible: true,
      selfReferral: patient.selfReferral,
      partnerID: patient.partnerID,
      partnerName: patient.partnerName,
    });
  }

  function updateReferralSourceChanged(type, partnerID, partnerName) {
    if (type === "self") {
      setUpdateReferralSource({
        ...updateReferralSource,
        selfReferral: true,
        partnerID: null,
        partnerName: null,
      });
    } else {
      setUpdateReferralSource({
        ...updateReferralSource,
        selfReferral: false,
        partnerID: partnerID,
        partnerName: partnerName,
      });
    }
  }

  function updateReferralSourceConfirm() {
    let u = context.addSpiner();
    axios.post('/api/admin/patients/updateReferralSource', {
      id: patient.id,
      selfReferral: updateReferralSource.selfReferral,
      partnerID: updateReferralSource.partnerID,
      partnerName: updateReferralSource.partnerName,
    }).then((response) => {
      setPatient({
        ...patient,
        selfReferral: updateReferralSource.selfReferral,
        partnerID: updateReferralSource.partnerID,
        partnerName: updateReferralSource.partnerName,
      });
      setUpdateReferralSource({
        visible: false,
        selfReferral: false,
        partnerID: null,
        partnerName: null,
      });
    }).catch((e) => {
      context.addError(e);
      context.removeSpiner(u);
    }).finally(() => {
      context.removeSpiner(u);
    });
  }

  // Documents
  function renderDocuments() {
    return (
      <div className="card">
        <div className="card-header sm">
          <FilesPreview ids={patient.files} allowDelete={true} />
        </div>
      </div>
    );
  }

  // Header
  function renderPatientStatusBlock() {
    if (patient.statusType === "DRAFT") {
      return (
        <Status color={"blue"} muted={true} text={"Draft"} />
      );
    }
    if (patient.statusType === "ACTIVE") {
      return (
        <Status color={"green"} muted={true} text={"Active"} />
      );
    }

    if (patient.statusType === "ARCHIVED") {
      return (
        <Status color={"grey"} muted={true} text={"Archived"} />
      );
    }

    if (patient.statusType === "CANCELED") {
      return (
        <Status color={"orange"} muted={true} text={"Unaligned"} />
      );
    }

    return (
      <Status color={"grey"} muted={true} text={"Archived"} />
    );
  }

  if (loading) {
    return (
      <div className="container">
        <div className="row">
          <div className="col-xs-12">
            <Loading />
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      <Helmet title={"Patient | " + patient.firstName + " " + (patient.middleName ? patient.middleName + " " : "") + patient.lastName} />

      <input ref={imageUploadRef} type="file" className="d-none" onChange={changeProfileImageInput} accept=".jpg,.jpeg,.png" multiple={false} />
      <input ref={fileUploadRef} type="file" className="d-none" onChange={changeFileInput} accept=".jpg,.jpeg,.png,.gif,.bmp,.tiff,.webp,.svg,.pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.odt,.ods,.odp,.txt,.csv,.zip,.rar" multiple={true} />

      {createProject.visible && (
        <div className="fixed-bottom modal-bg" onClick={(e) => closeCreateProjectModal()}>
          <div className="container" style={{paddingTop: '5%'}}>
            <div className="row">
              <div className="col-xs-10 col-xs-offset-1 col-lg-8 col-lg-offset-2">
                <div className="card shadowmore" onClick={(e) => {e.stopPropagation()}}>
                  <div className="card-header sm">
                    <span className="typo-strong-300">Create {createProject.template.title} project</span><br/>
                    <span className="typo-normal-175">Fill fields below and click create button</span>
                  </div>
                  <div className="card-content sm">

                    <div className="d-flex justify-content-start align-items-center">
                      <div className="w-50">
                        <div className="typo-strong-150">Patient <span className="text-red">*</span></div>
                        <PatientPicker
                          patientList={[patient]}
                          patientID={patient.id}
                          onChange={createProjectPatientUpdate}
                          onChangeID={null}
                          rightIcon={<ChevronDown height={16} width={16} color={'grey'} />}
                        />
                      </div>
                    </div>

                    <div className="d-flex justify-content-start align-items-center mt-3">
                      <div className="w-50">
                        <div className="typo-strong-150">Project Start Date</div>
                        <EditableDatePicker
                          date={createProject.createdAt}
                          onChange={createProjectDateUpdate}
                          rightIcon={<ChevronDown height={16} width={16} color={'grey'} />}
                        />
                      </div>
                    </div>

                    {/* {changeAssessmentStatusModalData.reasons.map((reason, ri) => {
                      return (
                        <div key={"reason_" + ri} className="side-item" onClick={() => changeAssessmentStatusConfirm(changeAssessmentStatusModalData.id, reason.id)}>
                          <div>
                            <div className="typo-strong-175">{reason.title}</div>
                            <div className="typo-normal-150">{reason.description}</div>
                          </div>
                        </div>
                      );
                    })} */}
                  </div>
                  <div className="card-content sm text-end">
                    <Button onClick={createProjectConfirm} color="blue" text="Create" />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {deleteProject.visible && deleteProject.project && (
        <div className="fixed-bottom modal-bg" onClick={() => closeDeleteProjectModal()}>
          <div className="container" style={{paddingTop: '5%'}}>
            <div className="row">
              <div className="col-xs-10 col-xs-offset-1 col-lg-6 col-lg-offset-3">
                <div className="card shadowmore" onClick={(e) => {e.stopPropagation()}}>
                  <div className="card-header sm">
                    <span className="typo-strong-300">Delete {deleteProject.project.templateName} project</span><br/>
                    <span className="typo-normal-175">This permanently deletes the project and all of its tasks. This cannot be undone.</span>
                  </div>
                  <div className="card-content sm">
                    {revertLabelForProject(deleteProject.project) && (
                      <div className="d-flex justify-content-start align-items-start">
                        <Alert height={16} width={16} color={'orange'} />
                        <div className="typo-normal-150 ms-2">{revertLabelForProject(deleteProject.project)}</div>
                      </div>
                    )}
                  </div>
                  <div className="card-content sm text-end">
                    <Button onClick={closeDeleteProjectModal} color="grey" text="Cancel" className="me-2" />
                    <Button onClick={deleteProjectConfirm} color="red" text="Delete Project" />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {deleteCaregiver.visible && deleteCaregiver.caregiver && (
        <div className="fixed-bottom modal-bg" onClick={() => closeDeleteCaregiverModal()}>
          <div className="container" style={{paddingTop: '5%'}}>
            <div className="row">
              <div className="col-xs-10 col-xs-offset-1 col-lg-6 col-lg-offset-3">
                <div className="card shadowmore" onClick={(e) => {e.stopPropagation()}}>
                  <div className="card-header sm">
                    <span className="typo-strong-300">Delete caregiver</span><br/>
                    <span className="typo-normal-175">This permanently removes the caregiver from this patient. This cannot be undone.</span>
                  </div>
                  <div className="card-content sm text-end">
                    <Button onClick={closeDeleteCaregiverModal} color="grey" text="Cancel" className="me-2" />
                    <Button onClick={deleteCaregiverConfirm} color="red" text="Delete Caregiver" />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {startPostAlignmentModal.visible && (
        <div className="fixed-bottom modal-bg" onClick={() => { if (!startPostAlignmentModal.submitting) closeStartPostAlignmentModal(); }}>
          <div className="container" style={{paddingTop: '5%'}}>
            <div className="row">
              <div className="col-xs-10 col-xs-offset-1 col-lg-6 col-lg-offset-3">
                <div className="card shadowmore" onClick={(e) => {e.stopPropagation()}}>
                  <div className="card-header">
                    <span className="typo-strong-300">Start Post-Alignment</span><br/>
                  </div>
                  <div className="card-content">
                    <div className="d-flex justify-content-start align-items-center">
                      <div className="typo-strong-150 me-2">Alignment Date:</div>
                      <div className="typo-normal-150">{patient.alignmentDate ? DateToFormat(DatetimeToDate(patient.alignmentDate), context.dateFormat) : '—'}</div>
                    </div>
                    <div className="d-flex justify-content-start align-items-center mt-1">
                      <div className="typo-strong-150 me-2">G-code:</div>
                      <div className="typo-normal-150">{patient.procedureCode || '—'}</div>
                    </div>
                    <div className="card red shadowless mt-2">
                      <div className="card-content sm">
                        <div className="d-flex justify-content-start align-items-start">
                          <Alert height={16} width={16} color={'red'} />
                          <div className="typo-normal-150 text-red ms-2">You will not be able to change the Alignment Date after Post-Alignment starts. Confirm the date above before continuing.</div>
                        </div>
                      </div>
                    </div>
                    <label className="d-flex align-items-start mt-2" style={{ cursor: startPostAlignmentModal.submitting ? 'not-allowed' : 'pointer' }}>
                      <input
                        type="checkbox"
                        className="me-2 mt-1"
                        checked={startPostAlignmentModal.confirmed}
                        disabled={startPostAlignmentModal.submitting}
                        onChange={(e) => setStartPostAlignmentModal(prev => ({ ...prev, confirmed: e.target.checked }))}
                      />
                      <span className="typo-normal-150">I confirm the Alignment Date is correct and cannot be changed after starting Post-Alignment.</span>
                    </label>
                  </div>
                  <div className="card-content sm text-end">
                    <Button onClick={closeStartPostAlignmentModal} color="grey" text="Cancel" className="me-2" disabled={startPostAlignmentModal.submitting} />
                    <Button onClick={confirmStartPostAlignment} color="blue" text="Start Post-Alignment" disabled={startPostAlignmentModal.submitting || !startPostAlignmentModal.confirmed} />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {unalignModal.visible && unalignModal.status && (
        <div className="fixed-bottom modal-bg" onClick={() => closeUnalignModal()}>
          <div className="container-fluid" style={{paddingTop: '5%'}}>
            <div className="row">
              <div className="col-xs-10 col-xs-offset-1 col-lg-6 col-lg-offset-3">
                <div className="card shadowmore" onClick={(e) => {e.stopPropagation()}}>
                  <div className="card-header">
                    <span className="typo-header-3xLarge">Unalign patient</span><br/>
                    <span className="typo-normal-175">
                      {(unalignModal.status.reasons || []).length > 0 ? 'Choose the unalignment date and reason' : 'Choose the unalignment date'}
                    </span>
                  </div>
                  <div className="card-content max-h-[70vh] overflow-y-auto">
                    <div className="mb-3">
                      <div className="typo-strong-150 mb-1">Unalignment date</div>
                      <EditableDatePicker
                        date={unalignModal.date}
                        onChange={(d) => setUnalignModal(prev => ({ ...prev, date: d ? formatDateYMD(d) : '' }))}
                        disabled={unalignModal.submitting}
                        clearable={false}
                        rightIcon={<ChevronDown height={16} width={16} color={'grey'} />}
                      />
                      {!unalignModal.date && (
                        <div className="typo-normal-125 mt-1" style={{ color: 'darkorange' }}>
                          Required before unaligning
                        </div>
                      )}
                    </div>
                    {(unalignModal.status.reasons || []).map((reason, ri) => (
                      <div
                        key={"unalign_reason_" + ri}
                        className={"side-item " + (unalignModal.reasonID === reason.id ? "active" : "")}
                        onClick={() => !unalignModal.submitting && setUnalignModal(prev => ({ ...prev, reasonID: reason.id }))}
                        style={{ opacity: unalignModal.submitting ? 0.5 : 1, cursor: unalignModal.submitting ? 'not-allowed' : 'pointer' }}
                      >
                        <div>
                          <div className="typo-strong-175">{reason.title}</div>
                          <div className="typo-normal-150">{reason.description}</div>
                        </div>
                      </div>
                    ))}
                    {(unalignModal.status.reasons || []).length > 0 && !unalignModal.reasonID && (
                      <div className="typo-normal-125 mt-2" style={{ color: 'darkorange' }}>
                        Required before unaligning
                      </div>
                    )}
                  </div>
                  <div className="card-content sm text-end">
                    <Button onClick={closeUnalignModal} color="grey" text="Cancel" className="me-2" disabled={unalignModal.submitting} />
                    <Button
                      onClick={submitUnalign}
                      color="blue"
                      text="Unalign"
                      disabled={unalignModal.submitting || !unalignModal.date || ((unalignModal.status.reasons || []).length > 0 && !unalignModal.reasonID)}
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {monthlyCheckinsModal.visible && patient && (
        <div className="fixed-bottom modal-bg" onClick={() => setMonthlyCheckinsModal({ visible: false })}>
          <div className="container" style={{paddingTop: '5%'}}>
            <div className="row">
              <div className="col-xs-10 col-xs-offset-1 col-lg-6 col-lg-offset-3">
                <div className="card shadowmore" onClick={(e) => {e.stopPropagation()}}>
                  <div className="card-header sm">
                    <span className="typo-strong-300">Monthly Check-ins</span>
                  </div>
                  <div className="card-content sm max-h-[70vh] overflow-y-auto">
                    {(patient.monthlyCheckins || []).length === 0 ? (
                      <div className="typo-normal-150 text-muted">No monthly check-ins recorded.</div>
                    ) : (
                      <>
                        <div className="d-flex justify-content-start align-items-center px-2 py-1">
                          <div className="typo-normal-150 text-muted" style={{width: '33%'}}>Check-in Period</div>
                          <div className="typo-normal-150 text-muted" style={{width: '33%'}}>Check-in Date</div>
                          <div className="typo-normal-150 text-muted" style={{width: '34%'}}>Checked In By</div>
                        </div>
                        {[...patient.monthlyCheckins].sort((a, b) => (a.date < b.date ? 1 : -1)).map((ci, ci_i) => (
                          <div key={"monthly_checkin_" + ci_i} className="side-item small nowrap">
                            <div className="typo-normal-150" style={{width: '33%'}}>{ci.period}</div>
                            <div className="typo-normal-150" style={{width: '33%'}}>{ci.date}</div>
                            <div className="typo-normal-150" style={{width: '34%'}}>{ci.createdByName || "—"}</div>
                          </div>
                        ))}
                      </>
                    )}
                  </div>
                  <div className="card-content sm text-end">
                    <Button onClick={() => setMonthlyCheckinsModal({ visible: false })} color="grey" text="Close" />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {estModal.visible && (
        <div className="fixed inset-0 z-2000 flex items-center justify-center bg-[#80808094]" onClick={estModal.submitting ? undefined : closeEstModal}>
          <div className="w-full max-w-md px-8">
            <div className="card shadowmore" onClick={(e) => e.stopPropagation()}>
              <div className="card-header">
                <span className="typo-header-3xLarge">G-code updated</span><br/>
                <span className="typo-normal-175 text-muted">
                  {patient.firstName} {patient.lastName}'s billing code was updated from {estModal.from} to {estModal.to} (Established patient rate).
                </span>
              </div>
              <div className="card-content">
                <div className="d-flex justify-content-end align-items-center mt-2">
                  <Button
                    text={estModal.submitting ? "Saving..." : "OK"}
                    color="blue"
                    disabled={estModal.submitting}
                    onClick={closeEstModal}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {cognitiveStagingForm.visible && patient && (
        <AssessmentFormModal
          assessmentID={cognitiveStagingForm.assessmentID}
          template={cognitiveStagingForm.template}
          patientID={patient.id}
          users={users}
          settings={settings}
          cognitiveStaging={true}
          onCognitiveStagingChange={handleCognitiveStagingChange}
          onClose={() => setCognitiveStagingForm({ visible: false, template: null, assessmentID: null })}
          onSaved={(finalAssessment) => {
            setAssessments((prevAssessments) => {
              if (cognitiveStagingForm.assessmentID) {
                return prevAssessments.map((a) => a.id === finalAssessment.id ? finalAssessment : a);
              }
              return [...prevAssessments, finalAssessment];
            });
            reloadCognitiveStagingStatus();
            setCognitiveStagingForm({ visible: false, template: null, assessmentID: null });
          }}
        />
      )}

      {createAssessment.visible && (
        <div className="fixed-bottom modal-bg" onClick={(e) => setCreateAssessment({ visible: false })}>
          <div className="container" style={{paddingTop: '5%'}}>
            <div className="row">
              <div className="col-xs-10 col-xs-offset-1 col-lg-6 col-lg-offset-3">
                <div className="card shadowmore" onClick={(e) => {e.stopPropagation()}}>
                  <div className="card-header sm">
                    <span className="typo-strong-300">Create assessment</span><br/>
                    <span className="typo-normal-175">Fill fields below and click create button</span>
                  </div>
                  <div className="card-content sm">
                    <div className="d-flex justify-content-start align-items-center">
                      <div className="w-50">
                        <div className="typo-normal-150">Assignee:</div>
                        <PhysicianPicker
                          physicianList={users}
                          physicianID={createAssessment.userID}
                          onChange={createAssessmentUserUpdate}
                          onChangeID={null}
                          // leftIcon={<UserEdit height={16} width={16} color={'grey'} />}
                          rightIcon={<ChevronDown height={16} width={16} color={'grey'} />}
                        />
                      </div>
                    </div>
                  </div>
                  <div className="card-content sm text-end">
                    <Button onClick={createAssessmentConfirm} color="blue" text="Create" />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {createAppointment.visible && (
        <div className="fixed-bottom modal-bg" onClick={(e) => setCreateAppointment({ visible: false })}>
          <div className="container" style={{paddingTop: '5%'}}>
            <div className="row">
              <div className="col-xs-10 col-xs-offset-1 col-lg-6 col-lg-offset-3">
                <div className="card shadowmore" onClick={(e) => {e.stopPropagation()}}>
                  <div className="card-header sm">
                    <span className="typo-strong-300">Create Appointment</span><br/>
                    <span className="typo-normal-175">Fill fields below and click create button</span>
                  </div>
                  <div className="card-content sm">

                    <input type="text" className="input borderless typo-strong-200 w-100 mb-2" placeholder="Title..." value={createAppointment.title} onChange={(e) => createAppointmentTitleUpdate(e.target.value)} autoFocus={true} />

                    <Editor
                      placeholder="Appointment description..."
                      value={createAppointment.description}
                      onBlur={createAppointmentDescriptionUpdate}
                      toolbar="limited"
                      className="text-editor borderless"
                    />

                    <div className="d-flex justify-content-start align-items-center mt-3">
                      <div className="w-50">
                        <div className="typo-normal-150">Assignee:</div>
                        <PhysicianPicker
                          physicianList={users}
                          physicianID={createAppointment.userID}
                          onChange={createAppointmentUserUpdate}
                          onChangeID={null}
                          rightIcon={<ChevronDown height={16} width={16} color={'grey'} />}
                        />
                      </div>
                    </div>
                  </div>
                  <div className="card-content sm text-end">
                    <Button onClick={createAppointmentConfirm} color="blue" text="Create" />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {createService.visible && (
        <div className="fixed-bottom modal-bg" onClick={(e) => { setLogDatesVisible(false); setCreateService({ visible: false }); }}>
          <div className="container" style={{paddingTop: '5%'}}>
            <div className="row">
              <div className="col-xs-10 col-xs-offset-1 col-lg-6 col-lg-offset-3">
                <div className="card shadowmore" onClick={(e) => {e.stopPropagation()}}>
                  <div className="card-header">
                    <span className="typo-header-3xLarge">Confirm Service Usage</span><br/>
                    <span className="typo-normal-175">Please select the partner, type of service provided and date of service</span>
                  </div>
                  <div className="card-content">
                    <div className="typo-normal-150 mt-3">Partner:</div>
                    <select
                      className="input sm typo-normal-150 w-100"
                      value={createService.partner ? createService.partner.id : ""}
                      onChange={(e) => {
                        const partner = partners.find((p) => p.id === e.target.value);
                        if (partner) {
                          serviceUpdatePartner(partner);
                        }
                      }}
                    >
                      {partners.filter(p => p.serviceProvider === true).map((partner, pi) => (
                        <option key={"partner_" + pi} value={partner.id}>{partner.title}</option>
                      ))}
                    </select>

                    <div className="typo-normal-150 mt-3">Type of service provided:</div>
                    {(createService.partner?.services || []).filter(s => s.status !== false).map((service, ri) => {
                      return (
                        <div key={"service_" + ri} className={"side-item " + (createService.service && (service.id === createService.service.id) ? "active" : "")} onClick={() => serviceUpdateService(service)}>
                          <div>
                            <div className="typo-strong-175">{service.title}</div>
                            <div className="typo-normal-150">— Withdraws ${service.price} from selected balance</div>
                          </div>
                        </div>
                      );
                    })}

                    <div className="typo-normal-150 mt-3">Date of service:</div>
                    <div className="d-flex flex-wrap justify-content-start align-items-center">
                      {createService.dates.map((date, di) => {
                        const canRemove = createService.dates.length > 1;
                        return (
                          <div key={"service_date_" + di} className="item grey me-2 mb-2 typo-normal-150" style={{cursor: canRemove ? 'pointer' : 'default'}}>
                            <span>{DateToFormat(date, context.dateFormat)}</span>
                            {canRemove && (
                              <span onClick={() => serviceRemoveDate(di)} style={{display: 'inline-flex'}}>
                                <Close height={14} width={14} color={'grey'} />
                              </span>
                            )}
                          </div>
                        );
                      })}

                      <div className="item typo-normal-150 mb-2" onClick={() => setLogDatesVisible(true)}><Plus height={16} width={16} color={'grey'} /> Log dates</div>
                    </div>
                  </div>
                  <div className="card-content text-end">
                    <Button onClick={serviceCreate} color="blue" text="Create" />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {logDatesVisible && (
        <div className="fixed-bottom modal-bg" onClick={(e) => { e.stopPropagation(); setLogDatesVisible(false); }}>
          <div className="container" style={{paddingTop: '5%'}}>
            <div className="row">
              <div className="col-xs-10 col-xs-offset-1 col-lg-4 col-lg-offset-4">
                <div className="card shadowmore" onClick={(e) => {e.stopPropagation()}} style={{width: 'fit-content', margin: '0 auto'}}>
                  <div className="card-header sm">
                    <span className="typo-strong-175">Log dates</span>
                  </div>
                  <div className="card-content sm">
                    <DateSelector date={null} dateSelected={serviceLogDate} />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {createMedication.visible && (
        <div className="fixed-bottom modal-bg" onClick={(e) => setCreateMedication({ ...createMedication, visible: false })}>
          <div className="container" style={{paddingTop: '5%'}}>
            <div className="row">
              <div className="col-xs-10 col-xs-offset-1 col-lg-8 col-lg-offset-2">
                <div className="card shadowmore" onClick={(e) => {e.stopPropagation()}}>
                  <div className="card-header sm">
                    <span className="typo-strong-300">Create medication</span><br/>
                    <span className="typo-normal-175">Fill fields below and click create button</span>
                  </div>
                  <div className="card-content sm">
                    <div className="d-flex justify-content-start align-items-center">
                      <div className="w-50">
                        <div className="typo-strong-150">Prescriber</div>
                        <PhysicianPicker
                          physicianList={users}
                          physicianID={createMedication.userID}
                          onChange={createMedicationUserUpdate}
                          onChangeID={null}
                          rightIcon={<ChevronDown height={16} width={16} color={'grey'} />}
                        />
                      </div>
                    </div>

                    <div className="d-flex justify-content-start align-items-center mt-3">
                      <div className="w-50">
                        <div className="typo-strong-150">Name <span className="text-red">*</span></div>
                        <input type="text" className="input sm typo-normal-150 w-100" placeholder="Medication name" value={createMedication.name} onChange={(e) => setCreateMedication({ ...createMedication, name: e.target.value })} autoFocus={true} />
                      </div>
                      <div className="w-50 ms-2">
                        <div className="typo-strong-150">Code</div>
                        <input type="text" className="input sm typo-normal-150 w-100" placeholder="Medication code" value={createMedication.code} onChange={(e) => setCreateMedication({ ...createMedication, code: e.target.value })} />
                      </div>
                    </div>

                    <div className="d-flex justify-content-start align-items-center mt-3">
                      <div className="w-33">
                        <div className="typo-strong-150">Dose</div>
                        <input type="text" className="input sm typo-normal-150 w-100" placeholder="e.g. 500 mg" value={createMedication.dose} onChange={(e) => setCreateMedication({ ...createMedication, dose: e.target.value })} />
                      </div>
                      <div className="w-33 ms-2">
                        <div className="typo-strong-150">Frequency</div>
                        <input type="text" className="input sm typo-normal-150 w-100" placeholder="e.g. BID" value={createMedication.frequency} onChange={(e) => setCreateMedication({ ...createMedication, frequency: e.target.value })} />
                      </div>
                      <div className="w-33 ms-2">
                        <div className="typo-strong-150">Route</div>
                        <input type="text" className="input sm typo-normal-150 w-100" placeholder="e.g. oral" value={createMedication.route} onChange={(e) => setCreateMedication({ ...createMedication, route: e.target.value })} />
                      </div>
                    </div>

                    <div className="mt-3">
                      <div className="typo-strong-150">Sig</div>
                      <textarea className="input sm typo-normal-150 w-100" rows="3" placeholder="Full instruction text" value={createMedication.sig} onChange={(e) => setCreateMedication({ ...createMedication, sig: e.target.value })} />
                    </div>

                    <div className="mt-3">
                      <div className="typo-strong-150">Indication</div>
                      <input type="text" className="input sm typo-normal-150 w-100" placeholder="Indication for use" value={createMedication.indication} onChange={(e) => setCreateMedication({ ...createMedication, indication: e.target.value })} />
                    </div>

                    <div className="d-flex justify-content-start align-items-center mt-3">
                      <div className="w-50">
                        <div className="typo-strong-150">Start Date <span className="text-red">*</span></div>
                        <EditableDatePicker
                          date={createMedication.start}
                          onChange={(date) => setCreateMedication({ ...createMedication, start: date })}
                          rightIcon={<ChevronDown height={16} width={16} color={'grey'} />}
                        />
                      </div>
                      <div className="w-50 ms-2">
                        <div className="typo-strong-150">End Date</div>
                        <EditableDatePicker
                          date={createMedication.end}
                          onChange={(date) => setCreateMedication({ ...createMedication, end: date })}
                          rightIcon={<ChevronDown height={16} width={16} color={'grey'} />}
                        />
                      </div>
                    </div>

                    <div className="d-flex justify-content-start align-items-center mt-3">
                      <div className="w-50">
                        <div className="typo-strong-150">Status</div>
                        <select className="input sm typo-normal-150 w-100" value={createMedication.status} onChange={(e) => setCreateMedication({ ...createMedication, status: e.target.value })}>
                          <option value="active">Active</option>
                          <option value="completed">Completed</option>
                          <option value="discontinued">Discontinued</option>
                          <option value="prn">PRN</option>
                          <option value="unknown">Unknown</option>
                        </select>
                      </div>
                      <div className="w-50 ms-2">
                        <div className="typo-strong-150">Source</div>
                        <select className="input sm typo-normal-150 w-100" value={createMedication.source} onChange={(e) => setCreateMedication({ ...createMedication, source: e.target.value })}>
                          <option value="provider">Provider</option>
                          <option value="patient">Patient</option>
                          <option value="external">External</option>
                        </select>
                      </div>
                    </div>

                    <div className="mt-3">
                      <div className="typo-strong-150">Notes</div>
                      <textarea className="input sm typo-normal-150 w-100" rows="3" placeholder="Additional notes" value={createMedication.notes} onChange={(e) => setCreateMedication({ ...createMedication, notes: e.target.value })} />
                    </div>
                  </div>
                  <div className="card-content sm text-end">
                    <Button onClick={createMedicationConfirm} color="blue" text="Create" />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {updateReferralSource.visible && (
        <div className="fixed-bottom modal-bg" onClick={(e) => setUpdateReferralSource({ visible: false })}>
          <div className="container" style={{paddingTop: '5%'}}>
            <div className="row">
              <div className="col-xs-10 col-xs-offset-1 col-lg-6 col-lg-offset-3">
                <div className="card shadowmore" onClick={(e) => {e.stopPropagation()}}>
                  <div className="card-header">
                    <span className="typo-header-3xLarge">Update referral source</span><br/>
                    <span className="typo-normal-175">Select a referral source and click update button</span>
                  </div>
                  <div className="card-content">
                    <div className="typo-normal-150">Referral source:</div>
                    <div className={"side-item " + (updateReferralSource.selfReferral ? "active" : "")} onClick={() => updateReferralSourceChanged("self", null, null)}>
                      <div>
                        <div className="typo-strong-175">Self Referral</div>
                        <div className="typo-normal-150">Patient referred themselves</div>
                      </div>
                    </div>
                    {partners.map((partner) => {
                      return (
                        <div key={"partner_"+partner.id} className={"side-item " + (updateReferralSource.partnerID === partner.id ? "active" : "")} onClick={() => updateReferralSourceChanged("partner", partner.id, partner.title)}>
                          <div>
                            <div className="typo-strong-175">{partner.title}</div>
                            <div className="typo-normal-150">{partner.description || "Partner referral"}</div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                  <div className="card-content text-end">
                    <Button onClick={updateReferralSourceConfirm} color="blue" text="Update" />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="container">

        <div className="row mb-4">
          <div className="col-xs-12">
            <div className="card shadowmore">
              {/* Header */}
              <div className="card-header sm">

                <div className="row">
                  <div className="col-xs-6 col-lg-6">
                    <div className="d-flex justify-content-start align-items-top">
                      <PatientAvatar src={patient.profileImageID} id={patient.id} alt="logo" style={{height: 60, width: 60}} onClick={triggerProfileImageInput} />
                      <div className="w-100 ps-2">
                        <div className="typo-strong-300">{patient.firstName} {patient.middleName ? patient.middleName + " " : ""}{patient.lastName}
                          {patient.statusType === "ACTIVE" && alignmentMonths() !== null && (
                            <span className="typo-normal-150 text-muted ms-2">{alignmentMonths()} month{alignmentMonths() === 1 ? "" : "s"} in program</span>
                          )}
                        </div>
                        <div className="d-flex justify-content-start align-items-center gap-2">
                          <div className="item grey typo-normal-150" onClick={openUpdateReferralSourceModal}><Tag height={14} width={16} color={'grey'} /> {patient.selfReferral ? "Self Referral" : patient.partnerName ? "By " + patient.partnerName : "No Referral Source"} <ChevronDown height={14} width={16} color={'grey'} /></div>
                          <PhysicianPicker
                            physicianList={users}
                            physicianID={patient.userID}
                            onChange={updatePatientUser}
                            onChangeID={patient.id}
                            rightIcon={<ChevronDown height={14} width={14} color={'grey'} />}
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="col-xs-6 col-lg-6">
                    <div className="d-flex justify-content-end align-items-center w-100 gap-2" style={{minHeight: 40}}>
                      {patient.missingRequiredActionCount > 0 && (
                        <Status
                          color={"red"}
                          muted={true}
                          text={patient.missingRequiredActionCount === 1 ? "Missing required action" : `${patient.missingRequiredActionCount} missing required actions`}
                        />
                      )}
                      {renderPatientStatusBlock()}
                      <Drop close={closePatientStatusDrop} side={"right"} className="ms-1">
                        <div className="item rect">
                          <MoreVertical height={14} width={16} color={'grey'} />
                        </div>
                        <div className="card" style={{width: 220}}>
                          <div className="card-header sm">
                            {/* <div className="typo-normal-150 text-muted">Change status:</div>
                            {renderProjectNextStatuses()}
                            <div className="typo-normal-150 text-muted">Other actions:</div>
                            <div className="side-item small nowrap typo-medium-150" onClick={() => navigate("/project/" + project.id + "/export")}>
                              <div className="typo-normal-150">Export Project</div>
                            </div> */}
                            <div className="typo-normal-150 text-muted">Status actions:</div>
                            {patient.statusType === "DRAFT" && (
                              <div className="side-item small nowrap typo-medium-150" onClick={activePatient}>
                                <div className="typo-normal-150">Activate Patient</div>
                              </div>
                            )}
                            {(patient.statusType === "ACTIVE" || patient.statusType === "CANCELED") && (
                              <div className="side-item small nowrap typo-medium-150" onClick={archivePatient}>
                                <div className="typo-normal-150">Archive Patient</div>
                              </div>
                            )}
                            {patient.statusType === "ARCHIVED" && (
                              <div className="side-item small nowrap typo-medium-150" onClick={activePatient}>
                                <div className="typo-normal-150">Activate Patient</div>
                              </div>
                            )}
                          </div>
                          <div className="card-header sm">
                            <div className="typo-normal-150 text-muted">Other actions:</div>
                            <div className="side-item small nowrap typo-medium-150" onClick={() => navigate("/export2/" + patient.id)}>
                              <div className="typo-normal-150">Export</div>
                            </div>
                            <div className="side-item small nowrap typo-medium-150" onClick={() => setMonthlyCheckinsModal({ visible: true })}>
                              <div className="typo-normal-150">View Monthly Check-ins</div>
                            </div>
                          </div>

                        </div>
                      </Drop>
                    </div>
                    {/* <div className="d-flex justify-content-end align-items-center w-100">
                      <div className="item grey typo-normal-150" onClick={openUpdateReferralSourceModal}><Tag height={14} width={16} color={'grey'} /> Export</div>
                    </div> */}
                  </div>
                </div>

              </div>
              {/* Tabs */}
              <div className="card-content sm d-flex justify-content-between align-items-center">

                <div className="d-flex justify-content-start align-items-center">
                  {/* <div className="item rect">
                    <Filter height={14} width={16} color={'grey'} />
                  </div> */}
                  <div className={"item typo-normal-150 " +      (isPhasesTab() ? "active" : "")}                        onClick={() => switchTab(defaultPhaseStep())} >Phases</div>
                  <div className={"item typo-normal-150 ms-2 " + (actionTabFilter === "careplan" ? "active" : "")}     onClick={() => switchTab("careplan")}     >Care Plan</div>
                  <div className={"item typo-normal-150 ms-2 " + (actionTabFilter === "appointments" ? "active" : "")} onClick={() => switchTab("appointments")} >Appointments</div>
                  <div className={"item typo-normal-150 ms-2 " + (actionTabFilter === "assessments" ? "active" : "")}  onClick={() => switchTab("assessments")}  >Assessments</div>
                  {/* <div className={"item typo-normal-150 ms-2 " + (actionTabFilter === "medications" ? "active" : "")}  onClick={() => switchTab("medications")}  >Medications</div> */}
                  <div className={"item typo-normal-150 ms-2 " + (actionTabFilter === "accounts" ? "active" : "")}     onClick={() => switchTab("accounts")}     >Respite</div>
                  <div className={"item typo-normal-150 ms-2 " + (actionTabFilter === "documents" ? "active" : "")}    onClick={() => switchTab("documents")}    >Documents</div>
                </div>

                {actionTabFilter === "careplan" ? (
                  <Drop close={closeCreatePlanDrop} side={"right"}>
                    <div className="item typo-normal-150 ms-2"><FilePlus height={16} width={16} color={'grey'} /> Create Plan</div>
                    <div className="card shadowmore" style={{width: 'fit-content'}}>
                      <div className="card-header sm">
                        {settings.planTemplates && settings.planTemplates.sort((a, b) => a.title.localeCompare(b.title)).map((template) => {
                          return (
                            <div key={template.id} className="item" style={{minWidth: 260}} onClick={() => createPlanConfirm(template.id)}>
                              <div className="w-100">
                                <div className="typo-strong-175">{template.title}</div>
                                <div className="typo-normal-150">{template.description}</div>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  </Drop>
                ) : actionTabFilter === "assessments" ? (
                  patient?.statusType !== "ARCHIVED" ? (
                  <Drop close={closeCreateAssessmentDrop} side={"right"}>
                    <div className="item typo-normal-150 ms-2"><FilePlus height={16} width={16} color={'grey'} /> Create</div>
                    <div className="card shadowmore" style={{width: 'fit-content'}}>
                      <div className="card-header sm">
                        {settings.assessmentTemplates.sort((a, b) => a.title.localeCompare(b.title)).map((template) => {
                          return (
                            <div key={template.id} className="item" style={{minWidth: 260}} onClick={() => createAssessmentOnClick(template)}>
                              <div className="w-100">
                                <div className="typo-strong-175">{template.title}</div>
                                <div className="typo-normal-150">{template.description}</div>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  </Drop>
                  ) : (
                    <div className="item typo-normal-150 ms-2 disabled" style={{opacity: 0.5, cursor: 'not-allowed'}}><Lock height={14} width={14} color={'grey'} /> Create</div>
                  )
                ) : actionTabFilter === "appointments" ? (
                  patient?.statusType !== "ARCHIVED" ? (
                  <Drop close={closeCreateAppointmentDrop} side={"right"}>
                    <div className="item typo-normal-150 ms-2"><FilePlus height={16} width={16} color={'grey'} /> Create</div>
                    <div className="card shadowmore" style={{width: 'fit-content'}}>
                      <div className="card-header sm">
                        {settings.appointmentTemplates.sort((a, b) => a.title.localeCompare(b.title)).map((template) => {
                          return (
                            <div className="item" style={{minWidth: 260}} onClick={() => createAppointmentOnClick(template)}>
                              <div className="w-100">
                                <div className="typo-strong-175">{template.title}</div>
                                <div className="typo-normal-150">{template.description}</div>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  </Drop>
                  ) : (
                    <div className="item typo-normal-150 ms-2 disabled" style={{opacity: 0.5, cursor: 'not-allowed'}}><Lock height={14} width={14} color={'grey'} /> Create</div>
                  )
                ) : (actionTabFilter === "accounts" && accounts.length > 0) ? (
                  patient?.statusType !== "ARCHIVED" ? (
                  <div className="item typo-normal-150 ms-2" onClick={serviceOnModal}><FilePlus height={16} width={16} color={'grey'} /> Create</div>
                  ) : (
                    <div className="item typo-normal-150 ms-2 disabled" style={{opacity: 0.5, cursor: 'not-allowed'}}><Lock height={14} width={14} color={'grey'} /> Create</div>
                  )
                ) : actionTabFilter === "medications" ? (
                  <div className="item typo-normal-150 ms-2" onClick={createMedicationOnClick}><FilePlus height={16} width={16} color={'grey'} /> Create</div>
                ) : actionTabFilter === "documents" ? (
                  <div className="item typo-normal-150" onClick={() => triggerFileInput()}><FilePlus height={16} width={16} color={'grey'} /> Upload Document</div>
                ) : null}

              </div>
            </div>
          </div>
        </div>

        {/* Workflow steps */}
        {isPhasesTab() && (
        <div className="row mb-4">
          <div className="col-xs-12">
            <div className="card shadowmore">
              <div className="card-content sm d-flex justify-content-between align-items-center">
                <div className="d-flex justify-content-start align-items-center">
                  {(() => {
                    const postProject = getPostAlignmentProject();
                    const startReady = canStartPostAlignment();
                    const missingProfileCount = missingPaafCodes().length;
                    const missingCaregiverCount = caregiverPhaseWarnCount();
                    // An unaligned patient has no active lifecycle project, so both
                    // alignment steps are locked until they're re-aligned.
                    const unaligned = patient.lifecycleStage === 'UNALIGNED';
                    const archived = patient.statusType === 'ARCHIVED';
                    const steps = [
                      { key: 'profile',       label: 'Patient Info', warn: missingProfileCount },
                      { key: 'caregivers',    label: 'Caregiver Info', warn: missingCaregiverCount },
                      { key: 'preAlignment',  label: 'Pre-Alignment', locked: unaligned },
                      { key: 'postAlignment', label: startReady ? 'Start Post-Alignment' : 'Post-Alignment', locked: unaligned || archived || (!postProject && !startReady), startAction: !unaligned && !archived && startReady },
                    ];
                    return steps.map((step, i) => {
                      const active = actionTabFilter === step.key;
                      const warn = !step.locked && step.warn > 0;
                      return (
                        <div key={step.key} className="d-flex align-items-center">
                          {i > 0 && <div className="mx-1" style={{ width: 20, height: 1, background: '#e0e0e0' }} />}
                          <div
                            className={"item typo-normal-150 " + (active ? "active " : "") + (step.locked ? "disabled" : "") + (warn ? " red" : "")}
                            style={{
                              ...(step.locked ? { opacity: 0.5, cursor: 'not-allowed' } : {}),
                              ...(step.startAction ? { backgroundColor: '#e3f2fd', borderRadius: 6 } : {}),
                            }}
                            onClick={() => {
                              if (step.locked) return;
                              if (step.startAction) {
                                openStartPostAlignmentModal();
                              } else {
                                switchTab(step.key);
                              }
                            }}
                          >
                            {step.locked ? (
                              <Lock height={14} width={14} color={'grey'} />
                            ) : step.done ? (
                              <CheckLarge height={14} width={14} color={'green'} />
                            ) : warn ? null : (
                              <span className="typo-strong-150 text-muted">{i + 1}</span>
                            )}
                            {step.label}
                            {warn && <Counter color="red" text={step.warn} className="ms-1" />}
                          </div>
                        </div>
                      );
                    });
                  })()}
                </div>

                <div className="d-flex align-items-center">
                  {(actionTabFilter === "preAlignment" || actionTabFilter === "postAlignment") ? (() => {
                    const lifecycleAction = getLifecycleAction();
                    if (!lifecycleAction) return null;

                    if (actionTabFilter === "preAlignment" && lifecycleAction.action === 'beginPre') {
                      return (
                        <div className="item typo-normal-150 ms-2" onClick={() => createPreAlignmentProject(lifecycleAction)}>
                          <FilePlus height={16} width={16} color={'grey'} /> Create Pre-Alignment Project
                        </div>
                      );
                    }

                    return null;
                  })() : null}
                  {patient.statusType === "ACTIVE" && ["POST_ALIGNMENT_NEW", "POST_ALIGNMENT_EST"].includes(patient.lifecycleStage) && getActiveLifecycleProject() && (
                    <div className="item typo-normal-150 ms-2" style={{ color: '#6F2020' }} onClick={openUnalignModal}>
                      Unalign Patient
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
        )}

        <div className="row">
          <div className="col-xs-12">

            {actionTabFilter === "appointments" && (
              <div className="d-flex justify-content-between align-items-center mb-2">
                <div className="d-flex justify-content-start align-items-center ps-2">
                  <div className="typo-normal-125 me-1">Filter by:</div>
                  <Drop close={closeAppointmentStatusFilterDrop}>
                    <div className="item gap-no typo-normal-125 me-1">
                      Status: {appointmentsFilter.statusType.length === 5 ? "All" : appointmentsFilter.statusType.map(s => s.replace("_", " ").toLowerCase().replace(/\b\w/g, c => c.toUpperCase())).join(", ")} <ChevronDown height={12} width={12} color={'grey'} />
                    </div>
                    <div className="card" style={{width: 200}}>
                      <div className="card-header sm">
                        <div className={"side-item small typo-medium-150 mb-1 " + (appointmentsFilter.statusType.includes("TO_DO") ? "active" : "")} onClick={() => toggleAppointmentStatusFilter("TO_DO")}>
                          <div className="typo-normal-150">To Do</div>
                        </div>
                        <div className={"side-item small typo-medium-150 mb-1 " + (appointmentsFilter.statusType.includes("IN_PROGRESS") ? "active" : "")} onClick={() => toggleAppointmentStatusFilter("IN_PROGRESS")}>
                          <div className="typo-normal-150">In Progress</div>
                        </div>
                        <div className={"side-item small typo-medium-150 mb-1 " + (appointmentsFilter.statusType.includes("PAUSED") ? "active" : "")} onClick={() => toggleAppointmentStatusFilter("PAUSED")}>
                          <div className="typo-normal-150">Paused</div>
                        </div>
                        <div className={"side-item small typo-medium-150 mb-1 " + (appointmentsFilter.statusType.includes("COMPLETED") ? "active" : "")} onClick={() => toggleAppointmentStatusFilter("COMPLETED")}>
                          <div className="typo-normal-150">Completed</div>
                        </div>
                        <div className={"side-item small typo-medium-150 mb-1 " + (appointmentsFilter.statusType.includes("CANCELED") ? "active" : "")} onClick={() => toggleAppointmentStatusFilter("CANCELED")}>
                          <div className="typo-normal-150">Canceled</div>
                        </div>
                      </div>
                    </div>
                  </Drop>
                  <Drop close={closeAppointmentUserFilterDrop} className="me-1">
                    <div className="item gap-no typo-normal-125 me-1">
                      Assignee: {appointmentsFilter.userID.length === 0 ? "All" : (appointmentsFilter.userID[0] === context.id ? "Only me" : "All")} <ChevronDown height={12} width={12} color={'grey'} />
                    </div>
                    <div className="card" style={{width: 200}}>
                      <div className="card-header sm">
                        <div className={"side-item small typo-medium-150 mb-1 " + (appointmentsFilter.userID.length === 0 ? "active" : "")} onClick={setAppointmentUserFilterAll}>
                          <div className="typo-normal-150">All</div>
                        </div>
                        <div className={"side-item small typo-medium-150 mb-1 " + (appointmentsFilter.userID.length > 0 && appointmentsFilter.userID[0] === context.id ? "active" : "")} onClick={setAppointmentUserFilterMe}>
                          <div className="typo-normal-150">Only me</div>
                        </div>
                      </div>
                    </div>
                  </Drop>
                </div>
                <div className="d-flex justify-content-end align-items-center pe-2">
                  <div className="typo-normal-125 me-1">Sort by:</div>
                  <Drop close={closeAppointmentSorterDrop} side={"right"}>
                    <div className="item gap-no typo-normal-125">
                      Create date {appointmentsSorter.order === "asc" ? "(oldest first)" : "(newest first)"} <ChevronDown height={12} width={12} color={'grey'} />
                    </div>
                    <div className="card" style={{width: 200}}>
                      <div className="card-header sm">
                        <div className={"side-item small typo-medium-150 mb-1 " + (appointmentsSorter.order === "asc" ? "active" : "")} onClick={setAppointmentsSortOrderAsc}>
                          <div className="typo-normal-150"><ChevronUp height={14} width={14} color={'grey'} /> Oldest first</div>
                        </div>
                        <div className={"side-item small typo-medium-150 mb-1 " + (appointmentsSorter.order === "desc" ? "active" : "")} onClick={setAppointmentsSortOrderDesc}>
                          <div className="typo-normal-150"><ChevronDown height={14} width={14} color={'grey'} /> Newest first</div>
                        </div>
                      </div>
                    </div>
                  </Drop>
                </div>
              </div>
            )}

            {actionTabFilter === "assessments" && (
              <div className="d-flex justify-content-between align-items-center mb-2">
                <div className="d-flex justify-content-start align-items-center ps-2">
                  <div className="typo-normal-125 me-1">Filter by:</div>
                  <Drop close={closeAssessmentStatusFilterDrop}>
                    <div className="item gap-no typo-normal-125 me-1">
                      Status: {assessmentsFilter.statusType.length === 5 ? "All" : assessmentsFilter.statusType.map(s => s.replace("_", " ").toLowerCase().replace(/\b\w/g, c => c.toUpperCase())).join(", ")} <ChevronDown height={12} width={12} color={'grey'} />
                    </div>
                    <div className="card" style={{width: 200}}>
                      <div className="card-header sm">
                        <div className={"side-item small typo-medium-150 mb-1 " + (assessmentsFilter.statusType.includes("TO_DO") ? "active" : "")} onClick={() => toggleStatusFilter("TO_DO")}>
                          <div className="typo-normal-150">To Do</div>
                        </div>
                        <div className={"side-item small typo-medium-150 mb-1 " + (assessmentsFilter.statusType.includes("IN_PROGRESS") ? "active" : "")} onClick={() => toggleStatusFilter("IN_PROGRESS")}>
                          <div className="typo-normal-150">In Progress</div>
                        </div>
                        <div className={"side-item small typo-medium-150 mb-1 " + (assessmentsFilter.statusType.includes("PAUSED") ? "active" : "")} onClick={() => toggleStatusFilter("PAUSED")}>
                          <div className="typo-normal-150">Paused</div>
                        </div>
                        <div className={"side-item small typo-medium-150 mb-1 " + (assessmentsFilter.statusType.includes("COMPLETED") ? "active" : "")} onClick={() => toggleStatusFilter("COMPLETED")}>
                          <div className="typo-normal-150">Completed</div>
                        </div>
                        <div className={"side-item small typo-medium-150 mb-1 " + (assessmentsFilter.statusType.includes("CANCELED") ? "active" : "")} onClick={() => toggleStatusFilter("CANCELED")}>
                          <div className="typo-normal-150">Canceled</div>
                        </div>
                      </div>
                    </div>
                  </Drop>
                  <Drop close={closeAssessmentUserFilterDrop} className="me-1">
                    <div className="item gap-no typo-normal-125 me-1">
                      Assignee: {assessmentsFilter.userID.length === 0 ? "All" : (assessmentsFilter.userID[0] === context.id ? "Only me" : "All")} <ChevronDown height={12} width={12} color={'grey'} />
                    </div>
                    <div className="card" style={{width: 200}}>
                      <div className="card-header sm">
                        <div className={"side-item small typo-medium-150 mb-1 " + (assessmentsFilter.userID.length === 0 ? "active" : "")} onClick={setUserFilterAll}>
                          <div className="typo-normal-150">All</div>
                        </div>
                        <div className={"side-item small typo-medium-150 mb-1 " + (assessmentsFilter.userID.length > 0 && assessmentsFilter.userID[0] === context.id ? "active" : "")} onClick={setUserFilterMe}>
                          <div className="typo-normal-150">Only me</div>
                        </div>
                      </div>
                    </div>
                  </Drop>
                </div>
                <div className="d-flex justify-content-end align-items-center">
                  <div className="typo-normal-125">Sort by:&nbsp;</div>
                  <Drop close={closeAssessmentSorterDrop} side={"right"}>
                    <div className="item gap-no typo-normal-125">
                      Create date {assessmentsSorter.order === "asc" ? "(oldest first)" : "(newest first)"} <ChevronDown height={12} width={12} color={'grey'} />
                    </div>
                    <div className="card" style={{width: 200}}>
                      <div className="card-header sm">
                        <div className={"side-item small typo-medium-150 " + (assessmentsSorter.order === "asc" ? "active" : "")} onClick={setAssessmentsSortOrderAsc}>
                          <div className="typo-normal-150"><ChevronUp height={14} width={14} color={'grey'} /> Oldest first</div>
                        </div>
                        <div className={"side-item small typo-medium-150 " + (assessmentsSorter.order === "desc" ? "active" : "")} onClick={setAssessmentsSortOrderDesc}>
                          <div className="typo-normal-150"><ChevronDown height={14} width={14} color={'grey'} /> Newest first</div>
                        </div>
                      </div>
                    </div>
                  </Drop>
                </div>
              </div>
            )}

            {actionTabFilter === "profile" ? (
              <>
                <div className="d-flex justify-content-end align-items-center mb-2 px-1">
                  <div className="item typo-normal-150" onClick={() => navigate('/patient/'+patient.id)}>
                    <FileEdit height={16} width={16} color={'grey'} /> Edit
                  </div>
                </div>
                {renderPages()}
              </>
            ) : actionTabFilter === "caregivers" ? (
              renderCaregivers()
            ) : actionTabFilter === "careplan" ? (
              renderCarePlan()
            ) : actionTabFilter === "preAlignment" ? (
              renderPreAlignment()
            ) : actionTabFilter === "postAlignment" ? (
              renderPostAlignment()
            ) : actionTabFilter === "appointments" ? (
              renderAppointments()
            ) : actionTabFilter === "assessments" ? (
              renderAssessments()
            ) : actionTabFilter === "medications" ? (
              renderMedications()
            ) : actionTabFilter === "accounts" ? (
              renderAccounts()
            ) : (
              renderDocuments()
            )}
          </div>
        </div>

      </div>
    </>
  );
}

export default Patient;
